/**
 * AYUNEL お気に入り —— 收藏的存储层(新房 Horizon 4.1.4)。2026-09-14 起;2026-09-15 接账号模式(施工单第 9 步)。
 *
 * 全站唯一的收藏存储文件(方案 05-output/2026-09-13-お気に入り重做方案-经双脑复审.md §2.5.6):
 * 商品页的心、header 数字、收藏页列表都只经这里的 `store` 读写,不各写各的(§2.3.1 纪律 2)。
 * 由 snippets/ayunel-wishlist-boot.liquid 每页加载一次(<script type="module">);别从别的地方再引入本文件
 * —— asset 地址带 ?v=,两处引入 = 两份存储层、两份快照。
 *
 * 对外只有一个有状态的出口 `store`,六个方法(守卫 tests/test_theme_wishlist_store.py):
 *   has(variantId) / list() / count()   同步读内存快照(list 给的是副本,改它不影响存储)
 *   add(item) / remove(variantId)        返回 Promise<{ ok, reason }>,调用方始终只写一条 await 路径;
 *                                        ok:true 才算存进去,调用方拿到 ok 才改心 / 删卡(纪律 3「先存再改 UI」)。
 *                                        reason:'' 写成功 · 'unchanged' 本来就是这样(已在 / 本来不在)·
 *                                        'storage' 本机存储写不进(满了 / 被浏览器禁用)· 'invalid' 认不出的 id / 过不了服务端规则 ·
 *                                        'readonly' 主题编辑器里 / boot 岛缺失或坏了 ·
 *                                        账号模式另有:'transient' 这一次没存上(限流 / 服务器忙 / 认不出客人,下一次照发)·
 *                                        'bug' 前端发错了(不重试)· 'down' / 'unknown' 本次访问停发(开关关着 / 响应认不出 /
 *                                        超时结果不明)· 'owner' 这一页已不属于当前登录的人(提示刷新)
 *   subscribe(fn)                        收藏变了就调 fn({ source }),source = local(本页客人写)/ tab(别的标签页)/
 *                                        pageshow(游客:返回键从缓存恢复)/ sync(账号:服务器来的整单替换、返回键恢复、停发改快照);
 *                                        返回取消订阅的函数
 * 另导出只读辅助:failNotice(reason)(down / unknown / owner 的对客句,原文在 boot snippet 的 copy 岛)·
 *   wishlistPending()(账号模式首轮同步还没结果、本机也没有这位客人的缓存:收藏页别出空态)·
 *   syncStopped()(账号模式本次访问停发的原因;收藏页在 'owner' 时不出空态、改出 owner 那句)· 纯函数给守卫直接测。
 *
 * 模式(按 boot 岛 { owner, proxy } 定,resolveMode):owner 为空 → 游客;owner 是客户号 → 账号;boot 缺失 / 坏了 → 只读
 * (显示本机游客格、写一律 'readonly' —— 已登录却读不出身份时,绝不把点击写进游客格、日后并给别人)。
 *
 * 游客模式 = 本机 `ayunel_wishlist_v2`,格式与 live 逐字段一致:
 *   { version: 2, items: [...], updated_at } · 每件 8 个字段(product_id / product_handle / product_gid / variant_id /
 *   variant_gid / added_at / added_price(円整数)/ added_from)· 最新的在前 · 按 variant_id 去重(色码尺码级)· 最多 100 件。
 *   added_from 两种含义不同(智鹏 2026-09-14 拍板,改 9/13 拍板第 3 条):
 *     'pdp'        商品页收的 —— 颜色、尺码都是客人亲手选的;
 *     'collection' 商品卡上收的 —— 卡上只有颜色可选,存的是那个颜色的一个变体(尺码不算客人选的):
 *                  收藏页上它的尺码框空着、选了才能加购;商品页上它不算「这个尺码收藏了」;商品页再亲手收同一件 → 升级成 pdp(withAdded)。
 *   死约:
 *   - 每次写都在同一个同步任务里「现读 → 改 → 写回」,中间不 await:别的标签页刚写进去的,不会被本页的旧快照盖掉。
 *   - 写不进 → 返回 ok:false、快照不动。不学 live「删掉最早 10 件再重试」—— 那等于替客人删收藏。
 *   - 旧格式 v1 只在 v2 不存在时读入;v2 写成功后才删 v1。读的时候绝不回写(DEV 预览与 live 同源、共用这份存储)。
 *   - 坏 JSON / items 不是数组 → 当空,不抛错。
 *   - 别的标签页改了(storage 事件)/ 返回键从缓存恢复(pageshow.persisted)→ 重读,变了才通知订阅者。
 *   - 主题编辑器里(Shopify.designMode)只读:编辑器预览与店面同源,点了会写进客人真实的收藏。
 *
 * 账号模式(施工单第 9 步 · 2026-09-15 双脑复审定稿)= 收藏存在 Worker `ayunel-wishlist`(经店面 /apps/wishlist/*),
 *   本机只缓存当前登录人一份 `ayunel_wishlist_acct = { owner, items }` 用来首屏立刻画心。死约:
 *   - 身份:缓存只认 owner === 本页 boot 的 owner;删缓存只在页面初始化这一处(别人的 / 坏的),storage 事件、返回键、409、
 *     写回一律只读不删;写回前现读磁盘,已经是别人的就不写、本页停发(A 的旧页不许把 B 的缓存盖掉或删掉)。
 *   - 请求:同一台浏览器所有标签页排一条队(Web Locks 'ayunel-wishlist-account';没有就退回本页 promise 链),一次只发一个,
 *     锁内「现读 → 发 → 分类 → 提交」:别的标签页迟到的旧清单盖不掉新清单。不带 query、只发 JSON、跳转不跟(redirect:'manual')、
 *     10 秒超时连正文一起算;add / remove 带 keepalive(点完马上跳页也发得出去)。
 *   - 分类(classify,只认 body 的 code、不按状态码猜;经 Shopify 代理 5xx 会被换成 HTML 500 页 —— README 三):
 *     抛错 / 超时 → unknown;跳转 / 不是 JSON / 认不出的 JSON → down;owner_mismatch → owner;brake / unavailable / not_found → down;
 *     busy / ratelimit / unauthorized → transient;bad_request / too_large / unsupported_media_type / method_not_allowed → bug。
 *     unknown / down / owner = 本次访问停发(超时 ≠ 没存:结果不明时不许接着发相反的操作);transient / bug 只这一次失败。
 *   - 页面加载:缓存命中先画缓存 → 锁内现读游客格,按服务端三条规则过滤(坏件留在游客格、不发)→ 有可发的先写「合并待定」
 *     `ayunel_wishlist_merge = { owner, keys }` 再 /merge;**待定记不上(别人的标记占着 / 本机写不进)就不并**,只拉清单
 *     → 成功才删游客格里发出去的那几件(按完整记录比,不按 id;直接改原文、其余逐字不动)、**删干净了才清待定**;
 *     没成功(没停发)照样 /list。待定属于别人时,那几件不并给本页的人(A 的合并结果丢了、B 又登录)。
 *   - 停发且本页从没拿到过这位客人的清单 → 只读显示本机游客格(开关关着时没同步过的客人不至于一片空;绝不写进游客格)。
 *   - 返回键:进缓存前收起账号的心;恢复时现读缓存,是本人的才画回并再拉一次清单;本机有过缓存、现在没了 / 是别人的 →
 *     按 owner 停发;本机从没存下过缓存 → 心保持收起、只拉一次清单(由服务端判人)。按 owner 停发之后回来的成功回包一律不采纳。
 *   - 服务器来的整单替换用 source 'sync':订单打标像对 'tab' 一样不理(页面打开时不改车;打标等这台下一次改车再补)。
 *   - 服务端成功、本机缓存写不进 → 仍算成功(以服务端为准)。主题编辑器里不删、不写、不发请求。
 *
 * 同文件的自定义元素(都只经 store 读写;页面元素不拆进别的 asset —— 相对 import 会带不同的 ?v=,存储层就加载两份):
 *   <ayunel-wishlist-heart>  商品页购买行的心(第 2 步;结构与样式在 snippets/ayunel-wishlist-heart.liquid,守卫
 *                            tests/test_theme_pdp_wishlist_heart.py)
 *   <ayunel-wishlist-card-heart>  全站商品卡上的心(第 10 步;结构与样式在 snippets/ayunel-wishlist-card-heart.liquid,
 *                            由官方 blocks/_product-card-gallery.liquid 渲染;守卫 tests/test_theme_card_wishlist_heart.py)
 *   <ayunel-wishlist-page>   收藏页(第 3 步;结构与样式在 sections/ayunel-wishlist-page.liquid,取数 section
 *                            sections/ayunel-wishlist-results.liquid,守卫 tests/test_theme_wishlist_page.py)
 *   <ayunel-wishlist-count>  header 右上角的心与数字(第 4 步;结构与样式在 snippets/ayunel-header-wishlist.liquid,
 *                            守卫 tests/test_theme_header_wishlist.py)
 * 文件末尾另有「订单打标」(第 7 步):收藏件数 + 车里命中的收藏写进购物车属性 _ayn_wl,只经 window.aynMarkCart;
 *   口径与局限见 08-themes/wishlist-src/README.md,守卫 tests/test_theme_wishlist_cart_tag.py。
 */
import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';

const KEY = 'ayunel_wishlist_v2';
const KEY_V1 = 'ayunel_wishlist_v1';
const KEY_ACCT = 'ayunel_wishlist_acct'; // 账号缓存:{ owner, items },只存当前登录人一份
const KEY_MERGE = 'ayunel_wishlist_merge'; // 合并待定:{ owner, keys },合并发出去、结果还没确认的那几件
const MAX_ITEMS = 100;
const LOCK_NAME = 'ayunel-wishlist-account';
const SYNC_TIMEOUT_MS = 10000; // 账号请求超时(连读正文);超时 = 结果不明,本次访问停发
const PROXY_DEFAULT = '/apps/wishlist';
const PROXY_RE = /^\/apps\/[a-z0-9-]+$/;
// 服务端(08-plugins/wishlist-worker/src/worker.js)的三条规则,原样照抄 —— 合并前按它过滤坏件(README 八);守卫盯着两边一致
const ID_RE = /^\d{1,20}$/;
const HANDLE_RE = /^[a-z0-9][a-z0-9-]{0,254}$/;
const ISO_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,3})?Z$/;
// Worker 的 11 个 code(README 三)→ 前端怎么处理;不在这里的一律 down(fail-closed)
const SERVER_CODES = Object.freeze({
  owner_mismatch: 'owner',
  brake: 'down',
  unavailable: 'down',
  not_found: 'down',
  busy: 'transient',
  ratelimit: 'transient',
  unauthorized: 'transient',
  bad_request: 'bug',
  too_large: 'bug',
  unsupported_media_type: 'bug',
  method_not_allowed: 'bug',
});

/* ---------------- 纯函数(导出给守卫直接测;不碰存储、不碰页面) ---------------- */

/** 数字 / 数字字符串 / Shopify gid 统一成数字字符串;认不出的一律 ''。 */
export function toId(value) {
  let id = typeof value === 'number' && Number.isSafeInteger(value) && value >= 0 ? String(value) : value;
  if (typeof id !== 'string') return '';
  id = id.trim();
  if (id.startsWith('gid://shopify/')) id = id.slice(id.lastIndexOf('/') + 1);
  return /^\d{1,20}$/.test(id) ? id : '';
}

/** 任意来源的一件 → live 同形的 8 个字段;没有合法 variant_id 的返回 null。原件没有 added_at 时用 addedAt。 */
export function cleanItem(raw, addedAt = '') {
  if (!raw || typeof raw !== 'object') return null;
  const variantId = toId(raw.variant_id) || toId(raw.variant_gid);
  if (!variantId) return null;
  const productId = toId(raw.product_id) || toId(raw.product_gid);
  const price = Number.parseInt(raw.added_price, 10);
  return {
    product_id: productId,
    product_handle: typeof raw.product_handle === 'string' ? raw.product_handle : '',
    product_gid: productId ? `gid://shopify/Product/${productId}` : '',
    variant_id: variantId,
    variant_gid: `gid://shopify/ProductVariant/${variantId}`,
    added_at: typeof raw.added_at === 'string' && raw.added_at ? raw.added_at : addedAt,
    added_price: Number.isFinite(price) && price > 0 ? price : 0,
    added_from: raw.added_from === 'collection' ? 'collection' : 'pdp',
  };
}

/** 任意来源的一串 → 整理好的清单:逐件 cleanItem、按 variant_id 去重(留最前的 = 最新的)、截到 100 件。 */
function cleanList(list, addedAt) {
  if (!Array.isArray(list)) return [];
  const seen = new Set();
  const items = [];
  for (const one of list) {
    const item = cleanItem(one, addedAt);
    if (!item || seen.has(item.variant_id)) continue;
    seen.add(item.variant_id);
    items.push(item);
    if (items.length === MAX_ITEMS) break;
  }
  return items;
}

/** 存储原文 → 整理好的清单。 */
function parseItems(raw, addedAt) {
  let doc;
  try {
    doc = JSON.parse(raw);
  } catch {
    return [];
  }
  return doc && Array.isArray(doc.items) ? cleanList(doc.items, addedAt) : [];
}

/** 读 v2 原文。坏 JSON / items 不是数组 → 空清单,不抛错。 */
export function parseGuest(raw) {
  return parseItems(raw, '');
}

/** 读旧格式 v1 原文(外层与 v2 同形,旧版按 product_id 去重)→ 按 v2 规则整理;缺 added_at 的记成迁移时刻。 */
export function fromV1(raw, addedAt = new Date().toISOString()) {
  return parseItems(raw, addedAt);
}

/** 加一件:已在就原样返回(不改顺序),否则放最前、截到 100 件(挤掉最早的)。
 *  唯一例外:已在的那件是从商品卡收的(collection = 只选了颜色),这次是在商品页亲手选了尺码再收(pdp)
 *  → 同一件升级成 pdp,只改 added_from(位置、added_at 不动;同 Worker 的 add / merge「已在只许 collection → pdp」)。
 *  反过来(pdp 的再以 collection 加一次)不降级:客人选过的尺码不能被卡上的一下抹掉。 */
export function withAdded(items, item) {
  const at = items.findIndex((one) => one.variant_id === item.variant_id);
  if (at === -1) return [item, ...items].slice(0, MAX_ITEMS);
  if (items[at].added_from !== 'collection' || item.added_from !== 'pdp') return items;
  const next = [...items];
  next[at] = { ...items[at], added_from: 'pdp' };
  return next;
}

/** 删一件:本来不在就原样返回。 */
export function withRemoved(items, variantId) {
  const next = items.filter((one) => one.variant_id !== variantId);
  return next.length === items.length ? items : next;
}

/** 按 boot 岛 { owner, proxy } 选模式:owner 为空 → 'guest';是客户号 → 'account';boot 缺失 / 坏了 / owner 认不出 → 'readonly'。 */
export function resolveMode(boot) {
  if (!boot || typeof boot !== 'object' || typeof boot.owner !== 'string') return 'readonly';
  if (boot.owner === '') return 'guest';
  return ID_RE.test(boot.owner) ? 'account' : 'readonly';
}

/** 服务端能收的一件(合并 / 加心前按它过滤;规则与 Worker 逐字一致,README 八)。 */
export function mergeable(item) {
  return (
    Boolean(item) &&
    typeof item === 'object' &&
    typeof item.variant_id === 'string' &&
    ID_RE.test(item.variant_id) &&
    typeof item.product_id === 'string' &&
    ID_RE.test(item.product_id) &&
    typeof item.product_handle === 'string' &&
    HANDLE_RE.test(item.product_handle) &&
    typeof item.added_at === 'string' &&
    ISO_RE.test(item.added_at) &&
    !Number.isNaN(Date.parse(item.added_at))
  );
}

/** 服务端清单里的一件长得对不对(200 的正文要每件都对,才算 ok)。 */
function serverItemOk(x) {
  return (
    Boolean(x) &&
    typeof x === 'object' &&
    typeof x.variant_id === 'string' &&
    ID_RE.test(x.variant_id) &&
    typeof x.product_id === 'string' &&
    ID_RE.test(x.product_id) &&
    typeof x.product_handle === 'string'
  );
}

/** 一次账号请求的结果:只认 body 的 code、不按状态码猜(409 可能是 owner_mismatch / brake / unavailable,429 可能是
 *  ratelimit / busy)。200 且清单每件都对 → ok;认得出的 code → 对应处理;其余(未知 code、200 却清单坏、没有 JSON)→ down。 */
export function classify(status, json) {
  if (status === 200 && json && Array.isArray(json.items) && json.items.every(serverItemOk)) return { kind: 'ok', items: json.items };
  const code = json && typeof json === 'object' ? json.code : undefined;
  return { kind: typeof code === 'string' && Object.hasOwn(SERVER_CODES, code) ? SERVER_CODES[code] : 'down' };
}

/** 合并「发出去的是哪一件」:variant_id + added_at + added_from 三者都相同才算同一件(按 id 删会删掉客人刚重收的那件)。 */
function recordKey(item) {
  return `${item.variant_id}|${item.added_at}|${item.added_from}`;
}

/* ---------------- 游客适配器:本机存储 ---------------- */

function localStore() {
  try {
    return window.localStorage || null; // Safari 禁用网站数据时,光是读这个属性就会抛错
  } catch {
    return null;
  }
}

function removeKey(key) {
  try {
    localStore()?.removeItem(key);
  } catch {
    /* 删不掉就算了:只读路径都按 owner 核对,不会拿它当自己的 */
  }
}

/** 本机有没有这个 key(读不出当没有)。 */
function hasKey(key) {
  try {
    return localStore()?.getItem(key) != null;
  } catch {
    return false;
  }
}

function readJson(key) {
  try {
    const raw = localStore()?.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

const guestAdapter = {
  /** 读一次本机 → { items, fromV1 }。读不出一律当空;绝不回写。 */
  read() {
    const storage = localStore();
    if (!storage) return { items: [], fromV1: false };
    try {
      const raw = storage.getItem(KEY);
      if (raw) return { items: parseGuest(raw), fromV1: false };
      const legacy = storage.getItem(KEY_V1);
      if (legacy) return { items: fromV1(legacy), fromV1: true };
    } catch {
      /* 读不出 = 当空 */
    }
    return { items: [], fromV1: false };
  },

  /** 同一个同步任务里:现读 → change(items) → 写回。返回 { ok, reason, items }。 */
  write(change) {
    const storage = localStore();
    if (!storage) return { ok: false, reason: 'storage' };
    const current = this.read();
    const items = change(current.items);
    if (items === current.items) return { ok: true, reason: 'unchanged', items };
    try {
      storage.setItem(KEY, JSON.stringify({ version: 2, items, updated_at: new Date().toISOString() }));
    } catch {
      return { ok: false, reason: 'storage' };
    }
    if (current.fromV1) {
      try {
        storage.removeItem(KEY_V1);
      } catch {
        /* v2 已写成功,之后只读 v2;v1 删不掉也不影响 */
      }
    }
    return { ok: true, reason: '', items };
  },
};

/* ---------------- 模式与身份 ---------------- */

function readBoot() {
  try {
    const island = document.querySelector('script[data-ayn-wishlist-boot]');
    const boot = island ? JSON.parse(island.textContent) : null;
    return boot && typeof boot === 'object' ? boot : null;
  } catch {
    return null;
  }
}

const boot = readBoot();
const mode = resolveMode(boot);
const owner = mode === 'account' ? boot.owner : '';
const proxy = mode === 'account' && typeof boot.proxy === 'string' && PROXY_RE.test(boot.proxy) ? boot.proxy : PROXY_DEFAULT;
const editing = () => Boolean(window.Shopify?.designMode);
let stopped = ''; // 账号模式本次访问停发的原因:'' / 'down' / 'unknown' / 'owner'
let synced = false; // 账号模式:本页有这位客人的清单了(缓存命中 / 拿到过 ok / 别的标签页写来的)
let settled = mode !== 'account'; // 账号模式:页面加载那一轮同步结束了(成功与否)
let cacheSeen = false; // 账号模式:本机磁盘上有过这位客人的缓存(读到过 / 写成功过)—— 返回键恢复时「缓存没了」才说明换了人

/* ---------------- 账号适配器:缓存 + 请求 ---------------- */

/** 读账号缓存:只认本页 owner 的 → 清单;没有 / 别人的 / 坏的 → null。 */
function readAcct() {
  if (!owner) return null;
  const doc = readJson(KEY_ACCT);
  return doc && typeof doc === 'object' && doc.owner === owner && Array.isArray(doc.items) ? cleanList(doc.items, '') : null;
}

/** 写账号缓存:先现读磁盘,已经是别人的 → 'other'(不写);写不进 / 本机存储用不了 → 'fail'(不影响本页);写好 → 'ok'。 */
function writeAcct(items) {
  const doc = readJson(KEY_ACCT);
  if (doc && typeof doc === 'object' && typeof doc.owner === 'string' && doc.owner !== owner) return 'other';
  const storage = localStore();
  if (!storage) return 'fail';
  try {
    storage.setItem(KEY_ACCT, JSON.stringify({ owner, items }));
    cacheSeen = true;
    return 'ok';
  } catch {
    return 'fail';
  }
}

/** 合并待定:属于别人的那几件不并给本页的人;本页只在「没有 / 是自己的」时写,别人的留着不盖。 */
function readPending() {
  const doc = readJson(KEY_MERGE);
  return doc && typeof doc === 'object' && typeof doc.owner === 'string' && Array.isArray(doc.keys) ? doc : null;
}

/** 记下这一批属于本页的人。返回 true = 记上了;false = 记不上(别人的标记占着 / 本机写不进)。
 *  记不上就不许发合并:回包一旦丢了,这一批没有归属保护,会被并给下一个登录的人(2026-09-15 实现复审 Codex #1)。 */
function writePending(keys) {
  const current = readPending();
  if (current && current.owner !== owner) return false;
  const storage = localStore();
  if (!storage) return false;
  try {
    storage.setItem(KEY_MERGE, JSON.stringify({ owner, keys }));
    return true;
  } catch {
    return false;
  }
}

function clearPending() {
  const current = readPending();
  if (!current || current.owner === owner) removeKey(KEY_MERGE);
}

/** 同一台浏览器所有标签页一条队:锁内一次只跑一个任务。任务自己不会抛错(抛了当 unknown)。 */
let chain = Promise.resolve();
function withLock(task) {
  const run = async () => {
    try {
      return await task();
    } catch (error) {
      console.error('[ayunel-wishlist] sync failed', error);
      return { ok: false, reason: 'unknown' };
    }
  };
  const viaChain = () => {
    const next = chain.then(run, run);
    chain = next.then(
      () => {},
      () => {}
    );
    return next;
  };
  const locks = window.navigator?.locks;
  if (locks && typeof locks.request === 'function') {
    try {
      return locks.request(LOCK_NAME, run).catch(viaChain);
    } catch {
      return viaChain();
    }
  }
  return viaChain();
}

/** 本次访问停发。owner → 这一页已不属于当前登录的人,收起心;其余 → 本页从没拿到过这位客人的清单时,只读显示游客格。 */
function stop(kind) {
  if (stopped) return;
  stopped = kind;
  if (kind === 'owner') {
    // 快照改空,并且一定通知一次:返回键收起时快照本来就是空的,走 commit 会当「没变」不通知 ——
    // 收藏页就还挂着「还没有收藏」、不换成「请刷新」(2026-09-15 变异测试实测)
    snapshot = [];
    notify('sync');
  } else if (!synced) commit(guestAdapter.read().items, 'sync');
}

/** 发一次账号请求 → { kind, items? }。只在锁里调。 */
async function send(op, body) {
  if (stopped) return { kind: stopped };
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), SYNC_TIMEOUT_MS);
  let result;
  try {
    const res = await window.fetch(`${proxy}/${op}`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ owner, ...body }),
      redirect: 'manual',
      credentials: 'same-origin',
      keepalive: op === 'add' || op === 'remove',
      signal: controller.signal,
    });
    const type = (res.headers?.get('content-type') || '').split(';')[0].trim().toLowerCase();
    if (res.type === 'opaqueredirect') {
      result = { kind: 'down' };
    } else if (type !== 'application/json') {
      try {
        res.body?.cancel()?.catch(() => {}); // 店面的 HTML 错误页约 230KB:不读
      } catch {
        /* 取消不了也照样判 down */
      }
      result = { kind: 'down' };
    } else {
      let json = null;
      try {
        json = await res.json();
      } catch (error) {
        if (controller.signal.aborted) throw error; // 读正文时超时 = 结果不明
        json = null;
      }
      result = classify(res.status, json);
    }
  } catch {
    result = { kind: 'unknown' };
  } finally {
    clearTimeout(timer);
  }
  if (result.kind === 'down' || result.kind === 'unknown' || result.kind === 'owner') stop(result.kind);
  return result;
}

/** 服务器来的整单清单 → 写缓存 + 替换快照。磁盘上已经是别人的 → 本页按 owner 停发,不采纳。
 *  本页已按 owner 停发(返回键恢复时核出换了人 / 登出)→ 迟到的成功回包一律不采纳:不写回缓存、不把这位客人的收藏画回来
 *  (2026-09-15 实现复审 Codex #3)。 */
function adopt(serverItems, source) {
  if (stopped === 'owner') return false;
  const items = cleanList(serverItems, '');
  if (writeAcct(items) === 'other') {
    stop('owner');
    return false;
  }
  synced = true;
  commit(items, source);
  return true;
}

/** 游客格里这次能并的:过得了服务端规则、不在「别人的合并待定」里,最多 100 件。 */
function mergeBatch() {
  let guest = guestAdapter.read();
  if (guest.fromV1 && guest.items.length) {
    // 旧格式 v1 缺 added_at 的件,每读一次都补成「此刻」:先落成 v2 把时间定下来,合并后才删得准(不然每页重并一次)
    guestAdapter.write((items) => items.slice());
    guest = guestAdapter.read();
  }
  const pending = readPending();
  const blocked = new Set(pending && pending.owner !== owner ? pending.keys : []);
  return guest.items.filter((item) => mergeable(item) && !blocked.has(recordKey(item))).slice(0, MAX_ITEMS);
}

/** 合并成功后:只删游客格里发出去的那几件(完整记录一致才删),其余逐字原样 —— 直接改原文里的 items,
 *  不经整理函数(坏件、没发的不许被顺手丢掉或改写;2026-09-15 实现复审 Codex #4)。
 *  返回 true = 游客格里已经没有发出去的那几件(删掉了 / 本来就不在);false = 删不掉(本机写不进 / 读不出)。 */
function consumeGuest(sent) {
  const keys = new Set(sent.map(recordKey));
  const storage = localStore();
  if (!storage) return false;
  try {
    const raw = storage.getItem(KEY);
    const doc = raw ? JSON.parse(raw) : null;
    if (!doc || typeof doc !== 'object' || !Array.isArray(doc.items)) return true;
    const rest = doc.items.filter((one) => {
      const item = cleanItem(one, '');
      return !(item && keys.has(recordKey(item)));
    });
    if (rest.length === doc.items.length) return true;
    storage.setItem(KEY, JSON.stringify({ ...doc, items: rest, updated_at: new Date().toISOString() }));
    return true;
  } catch {
    return false;
  }
}

/** 页面加载那一轮:先并游客格,没并成就拉清单。 */
function startup() {
  return withLock(async () => {
    if (stopped) return;
    const batch = mergeBatch();
    // 记不上「合并待定」(别人的标记占着 / 本机写不进)→ 这一批不并、游客格原样,只拉清单
    if (batch.length && writePending(batch.map(recordKey))) {
      const merged = await send('merge', {
        items: batch.map(({ variant_id, product_id, product_handle, added_from, added_at }) => ({
          variant_id,
          product_id,
          product_handle,
          added_from,
          added_at,
        })),
      });
      if (merged.kind === 'ok') {
        // 游客格删干净了才清待定:删不掉就留着标记 —— 这几件不会被并给下一个登录的人,下一页本人再并一次(服务端去重)
        // (2026-09-15 实现复审 Codex #2)
        if (consumeGuest(batch)) clearPending();
        adopt(merged.items, 'sync');
        return;
      }
      if (stopped) return;
    }
    const listed = await send('list', {});
    if (listed.kind === 'ok') adopt(listed.items, 'sync');
  });
}

/** 返回键恢复后再拉一次清单。主题编辑器里不拉(不发请求、不写缓存;2026-09-15 实现复审 Codex #5 等三路同报)。 */
function refresh() {
  if (editing()) return Promise.resolve();
  return withLock(async () => {
    if (stopped) return;
    const listed = await send('list', {});
    if (listed.kind === 'ok') adopt(listed.items, 'sync');
  });
}

/** 客人点的加 / 删(账号模式)。 */
function accountWrite(op, payload, change) {
  if (editing()) return Promise.resolve({ ok: false, reason: 'readonly' });
  if (stopped) return Promise.resolve({ ok: false, reason: stopped });
  if (op === 'add' && !mergeable(payload)) return Promise.resolve({ ok: false, reason: 'invalid' });
  return withLock(async () => {
    if (stopped) return { ok: false, reason: stopped };
    const before = readAcct() ?? snapshot;
    const unchanged = change(before) === before;
    const body =
      op === 'add'
        ? {
            variant_id: payload.variant_id,
            product_id: payload.product_id,
            product_handle: payload.product_handle,
            added_from: payload.added_from,
          }
        : { variant_id: payload };
    const result = await send(op, body);
    if (result.kind !== 'ok') return { ok: false, reason: result.kind };
    if (!adopt(result.items, 'local')) return { ok: false, reason: stopped || 'owner' };
    return { ok: true, reason: unchanged ? 'unchanged' : '' };
  });
}

/* ---------------- store:唯一出口 ---------------- */

let snapshot = [];
const listeners = new Set();

function notify(source) {
  for (const listener of [...listeners]) {
    try {
      listener({ source });
    } catch (error) {
      console.error('[ayunel-wishlist] subscriber failed', error);
    }
  }
}

function commit(items, source) {
  if (JSON.stringify(items) === JSON.stringify(snapshot)) return;
  snapshot = items;
  notify(source);
}

function applyChange(change) {
  if (editing() || mode === 'readonly') return { ok: false, reason: 'readonly' };
  const result = guestAdapter.write(change);
  if (result.ok) commit(result.items, 'local');
  return { ok: result.ok, reason: result.reason };
}

export const store = Object.freeze({
  has(variantId) {
    const id = toId(variantId);
    return id !== '' && snapshot.some((one) => one.variant_id === id);
  },

  list() {
    return snapshot.map((one) => ({ ...one }));
  },

  count() {
    return snapshot.length;
  },

  /** item = { product_id, product_handle, variant_id, added_price(円整数), added_from: 'pdp' | 'collection' } */
  add(item) {
    const clean = cleanItem({ ...item, added_at: new Date().toISOString() });
    if (!clean) return Promise.resolve({ ok: false, reason: 'invalid' });
    if (mode === 'account') return accountWrite('add', clean, (items) => withAdded(items, clean));
    return Promise.resolve(applyChange((items) => withAdded(items, clean)));
  },

  remove(variantId) {
    const id = toId(variantId);
    if (!id) return Promise.resolve({ ok: false, reason: 'invalid' });
    if (mode === 'account') return accountWrite('remove', id, (items) => withRemoved(items, id));
    return Promise.resolve(applyChange((items) => withRemoved(items, id)));
  },

  subscribe(listener) {
    if (typeof listener !== 'function') return () => {};
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },
});

/** down / unknown / owner 的对客句(原文在 snippets/ayunel-wishlist-boot.liquid 的 copy 岛,全站一处);其余 ''。 */
let copy = null;
export function failNotice(reason) {
  if (copy === null) {
    try {
      const island = document.querySelector('script[data-ayn-wishlist-copy]');
      const doc = island ? JSON.parse(island.textContent) : null;
      copy = doc && typeof doc === 'object' ? doc : {};
    } catch {
      copy = {};
    }
  }
  const key = reason === 'down' || reason === 'unknown' ? 'down' : reason === 'owner' ? 'owner' : '';
  return key && typeof copy[key] === 'string' ? copy[key] : '';
}

/** 账号模式:页面加载那一轮还没结果、本机也没有这位客人的缓存 —— 此刻「空」不等于「没有收藏」。 */
export function wishlistPending() {
  return mode === 'account' && !synced && !stopped && !settled;
}

/** 账号模式本次访问停发的原因:'' 没停 / 'down' / 'unknown' / 'owner'(只读)。
 *  收藏页据此在「这一页已不属于当前登录的人」时不出「还没有收藏」的空态,改出 owner 那句(2026-09-15 实现复审 Codex #6)。 */
export function syncStopped() {
  return stopped;
}

/* ---------------- 初始化与事件 ---------------- */

if (mode === 'account') {
  if (!editing()) {
    // 页面初始化 = 唯一删账号缓存的地方:别人的 / 坏的
    const doc = readJson(KEY_ACCT);
    if (hasKey(KEY_ACCT) && !(doc && typeof doc === 'object' && doc.owner === owner)) removeKey(KEY_ACCT);
  }
  const cached = readAcct();
  synced = cached !== null;
  cacheSeen = cached !== null;
  snapshot = cached ?? [];
  if (!editing()) {
    startup().then(() => {
      settled = true;
      notify('sync'); // 收藏页据此收起「加载中」(清单没变也要通知一次)
    });
  } else {
    settled = true;
  }
} else {
  // 游客页 = 这台浏览器登出了:本机不留账号数据(有才删 —— 游客模式读的时候不做任何写)
  if (mode === 'guest' && !editing() && hasKey(KEY_ACCT)) removeKey(KEY_ACCT);
  snapshot = guestAdapter.read().items;
}

/** 本页采纳本机磁盘上这位客人的缓存(别的标签页刚写的 / 返回键恢复时现读的):本页从此算「有这位客人的清单」。 */
function adoptCache(items, source) {
  synced = true;
  cacheSeen = true;
  commit(items, source);
}

window.addEventListener('storage', (event) => {
  if (mode === 'account') {
    if (stopped) {
      if (stopped === 'owner') return;
      // 停发的页(down / unknown)也认别的标签页写来的本人缓存 —— 那边同步成了,这边别还显示游客格(2026-09-15 实现复审);
      // 不看事件的 key:那边在同一个任务里先删游客格、再写缓存,只听缓存那一条会先闪一下「空」
      const mine = readAcct();
      if (mine) adoptCache(mine, 'tab');
      else if (!synced && (event.key === KEY || event.key === null)) commit(guestAdapter.read().items, 'tab');
      return;
    }
    if (event.key !== KEY_ACCT && event.key !== null) return;
    const items = readAcct(); // 别人的 / 没有 → 不采纳,也不删
    if (items) adoptCache(items, 'tab');
    return;
  }
  if (event.key === KEY || event.key === null) commit(guestAdapter.read().items, 'tab');
});

window.addEventListener('pagehide', (event) => {
  if (mode === 'account' && event.persisted) commit([], 'sync'); // 进返回键缓存前收起账号的心:恢复时先核对身份再画
});

window.addEventListener('pageshow', (event) => {
  if (!event.persisted) return;
  if (mode !== 'account') {
    commit(guestAdapter.read().items, 'pageshow');
    return;
  }
  const items = readAcct();
  if (stopped) {
    if (stopped === 'owner') return;
    if (items) adoptCache(items, 'sync');
    else commit(synced ? [] : guestAdapter.read().items, 'sync');
    return;
  }
  if (!items) {
    // 本机有过这位客人的缓存、现在没了 / 成了别人的 = 这台浏览器登出过或换了人
    if (cacheSeen) {
      stop('owner');
      return;
    }
    // 本机从来没存下过(存不下 / 首轮就没同步成):没有缓存可核身份,心保持收起,只拉一次清单 —— 服务端按此刻登录的人回话
    // (换了人 → 409 → owner;登出 → 401 → 这一次失败)。原来一律当「换了人」停发(2026-09-15 实现复审两路同报)
    refresh();
    return;
  }
  adoptCache(items, 'sync');
  refresh();
});

/* ---------------- <ayunel-wishlist-heart>:商品页购买行的心 ---------------- */

/*
 * 结构全在 Liquid(服务端渲染、宿主双挂 morph skip、宿主 id 带商品 id),这里只改 class、aria 和文字,一个节点都不造、不删。
 * 死约(施工单第 2 步 · 方案 §2.3 / §2.3.1):
 * - 上色只有一个函数 #paint():当前变体收藏了 → .is-saved + 「削除」;没收藏、但同款别的变体收藏过 → 提示行显示
 *   最近收藏的那一个的提示句(前段色码尺码由 Liquid 拼好、固定句尾也在 Liquid 里);组合不存在 → aria-disabled +
 *   读屏补充说明(这两个属性只由这里挂 —— 服务端不输出,免得 JS 生效前 / 加载失败时读屏把能用的心念成「不可用」)。
 * - 点心时写哪个变体,一律以同一表单 input[name=id] 当时的值为准(= カートに追加 要加的那个);
 *   表单里的变体不在本商品的变体表里(宿主和表单不是同一个商品)→ 当成没有,不写。
 *   选择事件只管上色 —— 写入对不对不依赖事件先后;模块加载前客人已换色这种边界点,点一次就自己纠正。
 * - 先存再改心(纪律 3):store 回 ok 才上色、才读屏播报、才发埋点;写不进 → 心保持原样,提示行临时换成失败句(红字)。
 * - 选择事件:只认本页这个商品(商品卡 / 别的弹窗 / 别的商品发来的不理);事件在请求发出前就派发,一到就标
 *   aria-busy;每次选择递增代号,只认最新一代的结果。
 * - 请求在途时点心不丢(2026-09-14 对抗审:以前是悄悄吞掉,客人点了没反应;2026-09-15 起「写入在途」同样算 —— 账号模式
 *   要等服务器):记下按钮当时写着的动作(追加 / 削除),
 *   结果回来后按那时的表单变体执行一次 —— 同官方「カートに追加」的排队(assets/product-form.js handleSubmit)。
 *   按「当时写着的动作」而不是「到时候再切换」:读屏用户按下的是「お気に入りに追加」,就只会发生追加。
 *   组合不存在 / 请求失败 → 这一下作废(官方排队同样作废,结果不明不替客人写)。
 * - 主题编辑器里 store 只读(reason 'readonly'):点了不写、不提示、不发埋点。
 * - 从商品卡收的那条(added_from 'collection',第 10 步):卡上只有颜色可选,这款要是还有颜色以外要选的(Liquid 给宿主挂
 *   data-card-partial),它的尺码就不是客人选的 —— 不让心变实心、不让点心变成「削除」(否则一打开商品页就替客人声明「S 收藏了」,
 *   点一下还会把卡上收的那条删掉);提示行只说颜色(clead,已定稿的「{option}を」句式;只有尺码一项的商品连颜色都没有,不说);
 *   在它那个变体上点心 = 亲手收下这个尺码,存储层把它升级成 pdp(不发 wishlist_add);在同色别的尺码上点心 = 新收那一件、
 *   卡上那条由它取代(#supersede,发一次 wishlist_remove,source 'pdp_refine')。只有颜色一项的商品,卡上收的那一件就是完整的
 *   选择,照常算(与收藏页下拉框的 initialChoice 同一条规则:只预选颜色和只有一个值的项,别的都算没选)。
 *   在途时记下的「削除」回来落在一个不算亲手收藏的变体上 → 什么都不做(不许顺手删卡上那件)。
 */
const HEART_TAG = 'ayunel-wishlist-heart';
const FAIL_MS = 10000; // 失败句显示多久:与官方购买键报错同一时长(assets/product-form.js ERROR_MESSAGE_DISPLAY_DURATION)
const SAY_GAP_MS = 100; // 读屏播报区先清空、隔一拍再写:同一句连说两次(连续收藏两个色码 / 连续失败),读屏才会再念一次

function track(name, params) {
  try {
    window.aynTrack?.(name, params); // 全站唯一埋点出口(assets/ayunel-track.js);埋点坏了不影响收藏
  } catch {
    /* 吞掉:埋点失败不许冒泡进收藏流程 */
  }
}

class AyunelWishlistHeart extends HTMLElement {
  #controller = null;
  #generation = 0;
  #variant = '';
  #variants = {};
  #selecting = false;
  #writing = false;
  #failing = false;
  #failText = ''; // 这次失败的句子:收藏失败 / 取消收藏失败各一句(brand-crm 2026-09-14 补跑框定稿,见 Liquid 的 data-label-fail*)
  #failTimer = 0;
  #sayTimer = 0;
  #queued = ''; // 请求在途时点的那一下:'add' | 'remove'
  #queueTimer = 0;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.#variants = this.#readVariants();
    this.closest('.shopify-section, dialog')?.addEventListener(StandardEvents.productSelect, this.#onSelect, { signal });
    this.#button()?.addEventListener('click', this.#onClick, { signal });
    window.addEventListener('pageshow', this.#resync, { signal });
    const unsubscribe = store.subscribe(() => this.#paint());
    signal.addEventListener('abort', unsubscribe);
    this.#resync();
  }

  disconnectedCallback() {
    this.#generation += 1;
    this.#queued = '';
    this.#controller?.abort();
    clearTimeout(this.#failTimer);
    clearTimeout(this.#sayTimer);
    clearTimeout(this.#queueTimer);
  }

  #button() {
    return this.querySelector('.ayunel-wl-heart__btn');
  }

  /** Liquid 给的每个变体 { price(円整数), lead(提示句前段) };读不出当空(心照样能用,只是没有提示句、价格记 0)。 */
  #readVariants() {
    try {
      const data = JSON.parse(this.querySelector('script[data-ayn-wl-variants]')?.textContent || '{}');
      return data && typeof data === 'object' ? data : {};
    } catch {
      return {};
    }
  }

  /** 同一表单隐藏 input[name=id] 此刻的值;组合不存在时官方把它写成 ''。
   *  变体表读得出来、表单里的变体却不在里面 = 宿主和表单不是同一个商品(换商品时宿主没跟着换)→ 当成没有:
   *  宁可这一下不写,也不把 A 商品的 id 配上 B 商品的变体写进收藏(2026-09-14 对抗审)。 */
  #formVariant() {
    const input = this.closest('form')?.querySelector('input[name="id"]');
    const id = input ? toId(input.value) : '';
    const known = Object.keys(this.#variants).length > 0;
    if (id && known && !Object.prototype.hasOwnProperty.call(this.#variants, id)) return '';
    return id;
  }

  #resync = () => {
    this.#variant = this.#formVariant();
    this.#paint();
  };

  #onSelect = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const owner = target.closest('product-card, quick-add-dialog');
    if (owner && !owner.contains(this)) return;
    const productId = String(event.product?.id ?? '').split('/').pop();
    if (productId && productId !== this.dataset.productId) return;
    const promise = event.promise;
    if (!promise || typeof promise.then !== 'function') return;

    const generation = ++this.#generation;
    this.#selecting = true;
    this.#paint();
    promise.then(
      (result) => this.#settle(generation, result?.detail),
      () => this.#settle(generation, null) // AbortError(被更新的选择中止)/ 网络失败:不报错,回到表单此刻的变体
    );
  };

  #settle(generation, detail) {
    if (generation !== this.#generation) return; // 旧一代的结果(先发后到)一律作废
    this.#selecting = false;
    if (detail && (!detail.productId || String(detail.productId) === this.dataset.productId)) {
      this.#variant = toId(detail.resource?.id); // resource 为 null = 组合不存在 → ''
    } else {
      this.#variant = this.#formVariant();
    }
    this.#paint();

    const intent = this.#queued;
    this.#queued = '';
    if (!intent || !detail) return; // 请求失败:结果不明,在途时点的那一下作废
    // 官方 product-form 和这里挂在同一个 promise 上、谁先谁后不定:隔一拍,等它把表单 input 换成新变体再执行(写入仍以表单为准)
    clearTimeout(this.#queueTimer);
    this.#queueTimer = setTimeout(() => this.#tap(intent), 0);
  }

  #onClick = () => {
    this.#tap('');
  };

  /** 点心的唯一入口。intent:'' = 按存储切换(平常点击);'add' / 'remove' = 在途时记下的那一下。
   *  选择在途、写入在途(账号模式要等服务器)都一样:记下按钮当时写着的动作,轮到时执行一次(2026-09-15 实现复审:
   *  原来写入在途时再点一律吞掉,换了色码尺码再点也一样)。 */
  #tap(intent) {
    if (this.#selecting || this.#writing) {
      this.#queued = intent || (this.#savedHere(this.#variant) ? 'remove' : 'add');
      return;
    }
    this.#write(intent);
  }

  /** 写完之后:在途时记下的那一下隔一拍再执行(写入仍以那时的表单为准);写失败时它已作废,这里拿到的是 ''。 */
  #runQueued(intent) {
    if (!intent) return;
    clearTimeout(this.#queueTimer);
    this.#queueTimer = setTimeout(() => this.#tap(intent), 0);
  }

  /** 这一件是从商品卡收的、而这款还有颜色以外要选的(宿主 data-card-partial)→ 它的尺码不是客人选的。只认本商品的变体。 */
  #cardOnly(item) {
    return (
      item.added_from === 'collection' &&
      this.hasAttribute('data-card-partial') &&
      Object.prototype.hasOwnProperty.call(this.#variants, item.variant_id)
    );
  }

  /** 这个色码尺码是不是客人亲手收藏的:心的实心 / 点心是追加还是削除,都只按它判(卡上只记了颜色的那条不算)。 */
  #savedHere(variantId) {
    return variantId !== '' && store.list().some((item) => item.variant_id === variantId && !this.#cardOnly(item));
  }

  async #write(intent) {
    const variantId = this.#formVariant();
    this.#variant = variantId;
    if (!variantId) {
      this.#paint();
      return;
    }
    const button = this.#button();
    const removing = intent ? intent === 'remove' : this.#savedHere(variantId);
    // 在途时记下的「削除」,结果回来停在了一个不算亲手收藏的变体上(没收过 / 卡上只记了颜色的那件):什么都不做 ——
    // 客人按下时看到的「削除」是上一个变体的,不许顺手把卡上那件删了(2026-09-15 独立审查 F3)
    if (removing && !this.#savedHere(variantId)) {
      this.#paint();
      return;
    }
    // 这个变体已在收藏里、却不算「亲手收藏」= 卡上收的那件:这次是把它升级成 pdp,不是新收一件
    const upgrading = !removing && store.has(variantId);
    this.#writing = true;
    this.#paint();
    const result = await (removing
      ? store.remove(variantId)
      : store.add({
          product_id: this.dataset.productId,
          product_handle: this.dataset.productHandle,
          variant_id: variantId,
          added_price: this.#variants[variantId]?.price,
          added_from: 'pdp',
        }));
    this.#writing = false;
    const queued = this.#queued;
    this.#queued = '';
    if (!result.ok) {
      // 在途时点的那一下作废:这次结果不明 / 没存上,不替客人接着写(同选择失败)
      if (result.reason === 'readonly') this.#paint();
      else this.#fail(removing, result.reason);
      return;
    }
    if (this.#failing) {
      this.#failing = false; // 上一次失败的红字不能压在这次成功上
      clearTimeout(this.#failTimer);
    }
    this.#paint();
    this.#say(removing ? button?.dataset.labelRemoved : button?.dataset.labelAdded);
    this.#runQueued(queued);
    if (result.reason !== '') return; // 'unchanged' = 别的标签页已经这样了,本次没有新增 / 删除,不计数
    // 升级不发 wishlist_add:卡上收的那次已经发过,收藏件数也没变;看板「加 − 删」按件算,再发一次就凭空多一件。
    // 但客人在这里亲手确认了尺码 —— 另发 wishlist_confirm(不计入加心次数)。收藏排行榜换新房后「色码尺码明细」只数客人选过尺码的,
    // 卡上收的只按颜色计;不发这条,在原尺码上确认的客人就永远进不了色码尺码,每色首个有货尺码被系统性少算
    // (智鹏 2026-09-15 定口径,独立审查 + Codex 复审查出;scripts/push_wishlist_board.py variant_cell)
    if (upgrading) {
      track('wishlist_confirm', {
        product_id: this.dataset.productId,
        product_handle: this.dataset.productHandle,
        variant_id: variantId,
        item_count: store.count(),
      });
      return;
    }
    if (removing) {
      track('wishlist_remove', {
        product_id: this.dataset.productId,
        product_handle: this.dataset.productHandle,
        item_count: store.count(),
      });
    } else {
      track('wishlist_add', {
        product_id: this.dataset.productId,
        product_handle: this.dataset.productHandle,
        variant_id: variantId,
        price: this.#variants[variantId]?.price,
        added_from: 'pdp',
        item_count: store.count(),
      });
      await this.#supersede(variantId);
    }
  }

  /** 商品页亲手收下了一件(新收,不是升级):卡上只记了同一个颜色的那条就多余了 —— 由这件取代,免得收藏页上同色两张卡
   *  (一张尺码空着、一张选好了;2026-09-15 独立审查 F2)。颜色按 Liquid 给的每变体 color 比(这款没有カラー = 都是 '',
   *  卡上那条连颜色都没选,也被取代)。每真删一件发一次 wishlist_remove(source 'pdp_refine'),看板「加 − 删」才按件对得上;
   *  删不掉就留着(不报错:新收的那件已经存好了)。 */
  async #supersede(variantId) {
    const colour = this.#variants[variantId]?.color ?? '';
    for (const item of store.list()) {
      if (item.variant_id === variantId || !this.#cardOnly(item)) continue;
      if ((this.#variants[item.variant_id]?.color ?? '') !== colour) continue;
      const result = await store.remove(item.variant_id);
      if (!result.ok || result.reason !== '') continue;
      track('wishlist_remove', {
        product_id: item.product_id,
        product_handle: item.product_handle,
        variant_id: item.variant_id,
        item_count: store.count(),
        source: 'pdp_refine',
      });
    }
  }

  /** removing:这次写的是「取消收藏」→ 用取消失败那句(动词对上客人刚做的操作);否则用收藏失败那句。
   *  账号模式停发(开关关着 / 结果不明)或这一页已不属于当前登录的人 → 换成 failNotice 那两句(第 9 步)。 */
  #fail(removing, reason = '') {
    const button = this.#button();
    this.#failText = failNotice(reason) || (removing && button?.dataset.labelFailRemove) || button?.dataset.labelFail || '';
    this.#failing = true;
    clearTimeout(this.#failTimer);
    this.#failTimer = setTimeout(() => {
      this.#failing = false;
      this.#paint();
    }, FAIL_MS);
    this.#paint();
    this.#say(this.#failText);
  }

  /** 读屏播报:先清空、隔 SAY_GAP_MS 再写 —— 直接写同一句,多数读屏不会再念。 */
  #say(text) {
    const region = this.querySelector('[role="status"]');
    if (!region) return;
    clearTimeout(this.#sayTimer);
    region.textContent = '';
    if (text) {
      this.#sayTimer = setTimeout(() => {
        region.textContent = text;
      }, SAY_GAP_MS);
    }
  }

  /** 最近收藏的本商品那一件的提示句前段(store.list() 最新的在前):
   *  卡上收的(尺码不是客人选的)→ 只说颜色(就算正停在它那个变体上);连颜色都没有可说的(只有尺码一项)→ 看下一件;
   *  别的 → 色码尺码那句,当前这个变体自己跳过(它收藏了心就是实心,不用再说)。 */
  #leadFor(variantId) {
    for (const item of store.list()) {
      const info = this.#variants[item.variant_id];
      if (!info) continue;
      if (this.#cardOnly(item)) {
        if (typeof info.clead === 'string' && info.clead) return info.clead;
        continue;
      }
      if (item.variant_id === variantId) continue;
      return typeof info.lead === 'string' ? info.lead : '';
    }
    return '';
  }

  /** 唯一的上色函数:只改 class、aria 和文字。 */
  #paint() {
    const button = this.#button();
    if (!button) return;
    const variantId = this.#variant;
    const saved = this.#savedHere(variantId);
    button.classList.toggle('is-saved', saved);
    button.setAttribute('aria-label', (saved ? button.dataset.labelRemove : button.dataset.labelAdd) || '');
    if (variantId) {
      button.removeAttribute('aria-disabled');
      button.removeAttribute('aria-describedby');
    } else {
      button.setAttribute('aria-disabled', 'true');
      if (button.dataset.noteId) button.setAttribute('aria-describedby', button.dataset.noteId);
    }
    if (this.#selecting || this.#writing) button.setAttribute('aria-busy', 'true');
    else button.removeAttribute('aria-busy');

    // 提示行 = 前段(色码尺码,按变体换)+ 固定句尾(Liquid 写好,只切显隐);失败时前段换成失败句、句尾收起、整行红字
    const hint = this.querySelector('.ayunel-wl-heart__hint');
    const lead = hint?.querySelector('.ayunel-wl-heart__lead');
    if (!hint || !lead) return;
    const text = this.#failing ? this.#failText : saved ? '' : this.#leadFor(variantId);
    if (lead.textContent !== text) lead.textContent = text;
    const tail = hint.querySelector('.ayunel-wl-heart__tail');
    if (tail) tail.hidden = this.#failing || text === '';
    hint.classList.toggle('is-error', this.#failing);
    hint.hidden = text === '';
  }
}

if (!customElements.get(HEART_TAG)) customElements.define(HEART_TAG, AyunelWishlistHeart);

/* ---------------- <ayunel-wishlist-card-heart>:全站商品卡上的心 ---------------- */

/*
 * 结构与样式全在 snippets/ayunel-wishlist-card-heart.liquid(官方 blocks/_product-card-gallery.liquid 里 render 一行,全站商品卡同一处);
 * 这里只改 class、aria 与读屏播报,一个节点都不造、不删。
 * 死约(施工单第 10 步 · 智鹏 2026-09-14 拍板):
 * - 按「款」上色:这款在收藏里有任何一件(任何颜色尺码、任何来源)= 实心。
 * - 点实心 = 这款的每一件都删掉:逐件 store.remove(存储层对外六个出口不变),碰到删不掉的就停;
 *   每真删掉一件发一次 wishlist_remove(与收藏页逐件删同口径,看板的「加 − 删」才对得上)。
 * - 点空心 = 存卡上此刻代表的那个变体:官方 product-card 的 productPageUrl 上的 ?variant=(客人点色块后就是那个颜色的变体);
 *   卡片还没升级 / 链接上没有 → Liquid 给的商品默认变体。added_from 必须恰为 'collection'(存储层把别的值一律记成 'pdp',
 *   写错一个字就等于替客人声明「尺码选好了」)。价格取这个变体的円整数(口径死律 2,Liquid 已除 100)。
 * - 每次接上页面都按存储重画:筛选排序会搬动卡片、无限滚动与推荐位是后插进来的、官方分区缓存存的是带着旧状态的 HTML。
 * - 先存再改心(纪律 3):store 回 ok 才上色、播报、发埋点;写不进 → 心按存储原样、只播报失败句。主题编辑器里只读,点了不写不提示。
 * - 写入在途时再点不理(2026-09-15 第 9 步双脑复审定:不做排队 —— 卡上的心一点一个动作,排队的「当时动作」
 *   在按款上色下没有意义;在途时按钮挂 aria-busy,snippet 的 CSS 把它调淡,客人看得见「在处理」)。
 *   账号模式失败:停发 / 这一页已不属于当前登录的人 → 播报 failNotice 那两句;其余照旧失败句。
 *   整款删除删完按最新快照再查(最多 4 轮):账号模式要等服务器,期间别处可能又加了同款;查满 4 轮还有 = 删除失败。
 * - 失败时除了读屏播报,画面下方再出一条看得见的浮条(showNotice;智鹏 2026-09-15 看样图定「方案 1:画面下方浮条」)。
 *   浮条的结构与样式在 snippets/ayunel-wishlist-boot.liquid,全站一处;它只给眼睛看(aria-hidden),读屏不念两遍。
 */
const CARD_TAG = 'ayunel-wishlist-card-heart';
const NOTICE_OUT_MS = 260; // 浮条退场:220ms 的退场动画走完再藏(同尺码诊断的 toast)

/** 画面下方的失败浮条:写字、出现,FAIL_MS 后退场(再失败一次就重新计时)。页面上没有浮条(旧 boot / 编辑器预览)就什么都不做。 */
let noticeTimer = 0;
function showNotice(text) {
  const bar = document.querySelector('[data-ayn-wishlist-notice]');
  const slot = bar?.querySelector('[data-ayn-wishlist-notice-text]');
  if (!bar || !slot || !text) return;
  clearTimeout(noticeTimer);
  slot.textContent = text;
  bar.hidden = false;
  void bar.offsetWidth; // 先让「不再 hidden」落到版面上,再挂 data-visible,入场动画才会走
  bar.setAttribute('data-visible', '');
  noticeTimer = setTimeout(() => {
    bar.removeAttribute('data-visible');
    noticeTimer = setTimeout(() => {
      bar.hidden = true;
    }, NOTICE_OUT_MS);
  }, FAIL_MS);
}

class AyunelWishlistCardHeart extends HTMLElement {
  #controller = null;
  #prices = {};
  #writing = false;
  #sayTimer = 0;

  connectedCallback() {
    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.#prices = this.#readPrices();
    this.#button()?.addEventListener('click', this.#onClick, { signal });
    const unsubscribe = store.subscribe(() => this.#paint());
    signal.addEventListener('abort', unsubscribe);
    this.#paint();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = null;
    clearTimeout(this.#sayTimer);
  }

  #button() {
    return this.querySelector('.ayunel-card-heart__btn');
  }

  /** 各变体不同价时 Liquid 给的 { 变体 id: 円整数 };同价的商品没有这段(用宿主的 data-price)。读不出当空。 */
  #readPrices() {
    try {
      const data = JSON.parse(this.querySelector('script[data-ayn-wlc-prices]')?.textContent || '{}');
      return data && typeof data === 'object' ? data : {};
    } catch {
      return {};
    }
  }

  /** 这款在收藏里的每一件(最新的在前)。 */
  #saved() {
    const productId = toId(this.dataset.productId);
    return productId ? store.list().filter((item) => item.product_id === productId) : [];
  }

  /** 卡上此刻代表的变体:官方 product-card 的 productPageUrl(点过色块 = 那个颜色的变体)→ 没有就用 Liquid 给的默认变体。 */
  #variant() {
    let fromCard = '';
    try {
      const url = this.closest('product-card')?.productPageUrl;
      if (url) fromCard = toId(new URL(url, window.location.origin).searchParams.get('variant'));
    } catch {
      fromCard = ''; // 官方卡片还没升级 / 链接读不出:退回默认变体
    }
    return fromCard || toId(this.dataset.variantId);
  }

  #onClick = async () => {
    if (this.#writing) return;
    const saved = this.#saved();
    const removing = saved.length > 0;
    this.#writing = true;
    this.#paint();
    const { outcome, reason } = removing ? await this.#removeAll(saved) : await this.#add();
    this.#writing = false;
    this.#paint();
    if (outcome === 'readonly') return; // 主题编辑器里:不写、不提示
    const button = this.#button();
    if (outcome === 'ok') {
      this.#say(removing ? button?.dataset.labelRemoved : button?.dataset.labelAdded);
      return;
    }
    const text = failNotice(reason) || (removing ? button?.dataset.labelFailRemove : button?.dataset.labelFail);
    this.#say(text);
    showNotice(text); // 看得见的那一条:画面下方浮条(读屏已由上一行播报)
  };

  /** 收下卡上此刻代表的变体。返回 { outcome: 'ok' / 'fail' / 'readonly', reason }。 */
  async #add() {
    const variantId = this.#variant();
    const price = this.#prices[variantId] ?? this.dataset.price;
    const result = await store.add({
      product_id: this.dataset.productId,
      product_handle: this.dataset.productHandle,
      variant_id: variantId,
      added_price: price,
      added_from: 'collection',
    });
    if (!result.ok) return { outcome: result.reason === 'readonly' ? 'readonly' : 'fail', reason: result.reason };
    if (result.reason === '') {
      track('wishlist_add', {
        product_id: this.dataset.productId,
        product_handle: this.dataset.productHandle,
        variant_id: variantId,
        price: Number.parseInt(price, 10) || 0,
        added_from: 'collection',
        item_count: store.count(),
      });
    }
    return { outcome: 'ok', reason: '' };
  }

  /** 这款的每一件逐件删(最新的先删),碰到删不掉的就停。返回 { outcome: 'ok' / 'fail' / 'readonly', reason }。
   *  删完再按最新快照查一轮(账号模式要等服务器,期间别的设备 / 标签页可能又加了同款),最多再查 3 轮。 */
  async #removeAll(first) {
    let items = first;
    for (let round = 0; round < 4 && items.length; round++) {
      for (const item of items) {
        const result = await store.remove(item.variant_id);
        if (!result.ok) return { outcome: result.reason === 'readonly' ? 'readonly' : 'fail', reason: result.reason };
        if (result.reason !== '') continue; // 'unchanged' = 别的标签页已经删了,不计数
        track('wishlist_remove', {
          product_id: item.product_id,
          product_handle: item.product_handle,
          variant_id: item.variant_id,
          item_count: store.count(),
          source: 'collection',
        });
      }
      items = this.#saved();
    }
    // 查满 4 轮这款还有(别的设备 / 标签页一直在加):心仍实心,按删除失败说,不许播报「已删除」(2026-09-15 实现复审 Codex #7)
    if (items.length) return { outcome: 'fail', reason: '' };
    return { outcome: 'ok', reason: '' };
  }

  /** 读屏播报:先清空、隔 SAY_GAP_MS 再写(同商品页的心)。 */
  #say(text) {
    const region = this.querySelector('[role="status"]');
    if (!region) return;
    clearTimeout(this.#sayTimer);
    region.textContent = '';
    if (text) {
      this.#sayTimer = setTimeout(() => {
        region.textContent = text;
      }, SAY_GAP_MS);
    }
  }

  /** 唯一的上色函数:只改 class 与 aria。 */
  #paint() {
    const button = this.#button();
    if (!button) return;
    const saved = this.#saved().length > 0;
    button.classList.toggle('is-saved', saved);
    button.setAttribute('aria-label', (saved ? button.dataset.labelRemove : button.dataset.labelAdd) || '');
    if (this.#writing) button.setAttribute('aria-busy', 'true');
    else button.removeAttribute('aria-busy');
  }
}

if (!customElements.get(CARD_TAG)) customElements.define(CARD_TAG, AyunelWishlistCardHeart);

/* ---------------- <ayunel-wishlist-page>:收藏页 ---------------- */

/*
 * 结构全在 sections/ayunel-wishlist-page.liquid(商品卡、占位卡是两个 <template>),这里只:取数 → 从模板克隆 → 填字
 * (一律 textContent)→ 切 hidden 与按钮状态。对客日语全部从 data-* / 模板里拿,这里不写一个字。
 * 死约(施工单第 3 步 · 方案 §2.3 收藏页行):
 * - 取数:store.list() 里去重后的商品 id,每 50 个一次站内搜索 + Section Rendering API(sections/ayunel-wishlist-results.liquid)。
 *   自己 fetch:10 秒超时、cache no-store;不用官方 sectionRenderer —— 它不看状态码、没有超时、还会缓存
 *   (assets/section-renderer.js getSectionHTML)。res.ok + 根标记 + JSON 三样都对才算这一批成功;
 *   否则这一批「取数失败」—— 不等于商品不存在:照样出可删除的占位卡,收藏有变化时重取。
 *   已取到的商品按 id 缓存,只补取新出现的 id(账号模式下 list() 会先给缓存、再给服务器清单,subscribe 连着来两次)。
 * - 出卡按 list() 的顺序(最新收藏的在前):收藏的那个变体找得到 → 商品卡;找不到 → 占位卡。绝不退回 variants[0]
 *   (等于替客人换了一个色码尺码)。计数含占位卡。第一轮取数回来之前网格不出,免得先闪一排「取不到」;
 *   这段时间宿主挂 aria-busy="true"(读屏知道在加载),取数回来(成功或失败)就摘掉。
 * - 删除:await store.remove,ok 才删卡(由重画删掉 —— 本页删的、别的标签页删的走同一条路)、才播报、才发埋点;
 *   失败 → 卡留着,卡下错误行 + 读屏。收藏页没有任何分支会自动删客人的收藏(纪律 3)。
 * - 加购:照 2点セット(assets/ayunel-pdp-bundle.js)的契约 —— 只发 4.1.4 标准事件 CartLinesUpdateEvent,
 *   detail.items 给 /cart.js 的整车(页头数字 cart-icon 按它去掉赠品再求和);加购前后各读一次整车:服务器明确成功就算成功,
 *   回包丢了 / 超时才看前后差,加购前读不到车就不发;所有请求 10 秒超时;整页同时只许一个加购在途;不发 add_to_cart
 *   (Shopify 原生已在发,再发 = 双计数)。成功 → 按钮挂 .is-added,CSS 把平常的字换成模板里的「カートに追加しました」
 *   (两段叠在同一格,按钮不长高)2.4 秒(这期间这张卡不能再点),读屏播报取同一段字;不跳转 /cart、不从收藏里删。
 *   ⚠️ 同契约副本:addOne 是照 2点セット 另写的一份(页面元素不能 import 别的 asset;施工单不让改 pdp-bundle.js),
 *   登记在 08-themes/horizon-pdp-bundle-anatomy.md「同契约副本」;两份共同的契约由 tests/test_theme_wishlist_page.py
 *   test_cart_add_contract_shared_with_bundle_copy 一起钉 —— 改一处必须查另一处。已知差异(目前无功能影响):
 *   这里不带 itemCount(页头数字 cart-icon 优先读 items)、没有「新客先等别的加购」(收藏页上没有别的加购组件,整页加购另有 cartBusy 锁)。
 * - 埋点只走 window.aynTrack(参数名同 live):wishlist_view{item_count} 每次页面加载只发一次;
 *   wishlist_purchase_intent 加购成功才发;wishlist_remove 删除成功且确有删除('unchanged' 不算)才发。
 * - 颜色 / 尺码下拉框(第 10 步,智鹏 2026-09-14 拍板;排法、字号、「（在庫切れ）」标法照 2点セット 的下拉框):
 *   每个不止一个值的商品选项一个下拉框(只有一个值的项不出、直接算选好),第一次出卡时按 initialChoice 摆好
 *   (商品页收的 = 全部选好;商品卡收的 = 只选颜色、尺码空着);之后客人改的就留着 —— 重画(收藏变了 / 取数回来 / 成功句结束)
 *   不冲掉;加购在途 + 成功句那 2.4 秒锁住。只影响这次加购,不改收藏里存的那一件。
 *   加购的是下拉框此刻选中的那个变体:没选齐 → 键灰掉(字仍是「カートに追加」,点它 = 焦点带到空着的下拉框);
 *   选齐了买不到 / 没这个组合 / 按已选的怎么选都买不了(anyBuyable)→「在庫切れ」;排着队时改成加不了的 → 那一下作废、收起转圈。
 *   卡片的图、价格、链接跟着选择走(previewFor:没选齐 / 组合不存在时显示已选颜色的那一个)。有没有货只看前面几项的选择:
 *   第一项(颜色)按整色有没有货、只标「（在庫切れ）」不禁选(客人还能换过去看图);后面几项(尺码)买不到的禁选 —— 同 2点セット。
 * - 节点一律从模板克隆,不用 innerHTML / createElement;重画按 variant_id 复用已有卡片、只在顺序不对时移动
 *   (移动会让卡里的焦点丢掉)。下拉框与选项也从模板克隆(select / option 两个 <template>),选项文字一律 textContent。
 */
const PAGE_TAG = 'ayunel-wishlist-page';
const RESULTS_SECTION = 'ayunel-wishlist-results';
const RESULTS_BATCH = 50;
const FETCH_MS = 10000; // 取数 / 读车 / 加购请求的超时(同 2点セット)
const ADDED_MS = 2400; // 「カートに追加しました」停留多久(同 2点セット、live)
const REFETCH_MS = 3000; // 取数失败后隔多久自动重取一次(只一次;上线前复审 F2)
const CART_SOURCE = 'ayunel-wishlist-page';
const RESULTS_RE =
  /<div class="ayunel-wl-results" data-ayn-wl-results="1"[^>]*>\s*<script type="application\/json" data-ayn-wl-results-json>([\s\S]*?)<\/script>/;

/** 收藏清单里的商品 id → 每批 ≤50 个的取数地址 [{ ids, url }]。认不出的 id 不进请求(出卡时当占位),重复的只取一次。 */
export function buildResultUrls(productIds, searchUrl = '/search') {
  const ids = [...new Set((productIds || []).map(toId).filter(Boolean))];
  const batches = [];
  for (let i = 0; i < ids.length; i += RESULTS_BATCH) {
    const batch = ids.slice(i, i + RESULTS_BATCH);
    const params = new URLSearchParams();
    params.set('q', batch.map((id) => `id:${id}`).join(' OR '));
    params.set('type', 'product');
    params.set('options[unavailable_products]', 'show'); // 整款售罄的也要列出来(Shopify 默认 last 也列,只是排后面)
    params.set('options[prefix]', 'none'); // 最后一个 id 不做前缀匹配
    params.set('section_id', RESULTS_SECTION);
    batches.push({ ids: batch, url: `${searchUrl || '/search'}?${params}` });
  }
  return batches;
}

/** 站内路径(/ 开头)或 https 地址才要;别的一律当空。 */
function safeUrl(value) {
  return typeof value === 'string' && /^(\/|https:\/\/)/.test(value) ? value : '';
}

const isText = (value) => typeof value === 'string';

function cleanVariant(raw) {
  const id = toId(raw?.id);
  if (!id) return null;
  const src = safeUrl(raw.img?.src);
  return {
    id,
    label: isText(raw.label) ? raw.label : '',
    options: Array.isArray(raw.options) && raw.options.every(isText) ? raw.options : [],
    available: raw.available === true,
    price: isText(raw.price) ? raw.price : '',
    url: safeUrl(raw.url),
    img: src ? { src, srcset: isText(raw.img.srcset) ? raw.img.srcset : '' } : null,
  };
}

/** 商品的选项 [{ name, values[] }];形状不对、或有变体的选项值个数对不上 → [](这款不出下拉框,按收藏的那件原样加购)。 */
function cleanOptions(raw, variants) {
  if (!Array.isArray(raw)) return [];
  const options = raw.filter((one) => isText(one?.name) && Array.isArray(one.values) && one.values.every(isText));
  if (options.length !== raw.length) return [];
  return variants.every((variant) => variant.options.length === options.length) ? options : [];
}

/** 取数 section 的回包 → { ok, products:{ [商品 id]: 商品 } }。根标记没有 / JSON 坏了 / 不是清单 → { ok:false, products:null }
 *  (= 这一批取数失败,**不是**「这些商品不存在」)。 */
export function parseResults(html) {
  const match = typeof html === 'string' ? RESULTS_RE.exec(html) : null;
  if (!match) return { ok: false, products: null };
  let data;
  try {
    data = JSON.parse(match[1]);
  } catch {
    return { ok: false, products: null };
  }
  if (!data || !Array.isArray(data.products)) return { ok: false, products: null };
  const products = {};
  for (const raw of data.products) {
    const id = toId(raw?.id);
    if (!id || !Array.isArray(raw.variants)) continue;
    const variants = raw.variants.map(cleanVariant).filter(Boolean);
    products[id] = {
      id,
      handle: isText(raw.handle) ? raw.handle : '',
      title: isText(raw.title) ? raw.title : '',
      options: cleanOptions(raw.options, variants),
      variants,
    };
  }
  return { ok: true, products };
}

/**
 * 清单 + 已取到的商品 → 按清单顺序、每件一项的出卡结果(计数 = 清单件数,占位也算):
 *   { kind: 'card', item, product, variant }        收藏的那个变体找得到
 *   { kind: 'gone', item, reason: 'variant' }       商品在,那个色码尺码没了
 *   { kind: 'gone', item, reason: 'unavailable' }   商品取不到(取数成功但里面没有 / 这一批取数失败 / 认不出的 id)
 *   { kind: 'pending', item }                       还没取过
 * products:{ [商品 id]: 商品 | null },null = 取数成功但里面没有它;failed:这一批取数失败的商品 id(Set 或数组)。
 */
export function resolveCards(items, products = {}, failed = []) {
  const failedIds = failed instanceof Set ? failed : new Set(failed);
  return (items || []).map((item) => {
    const productId = toId(item?.product_id);
    if (!productId || failedIds.has(productId)) return { kind: 'gone', item, reason: 'unavailable' };
    if (!Object.prototype.hasOwnProperty.call(products, productId)) return { kind: 'pending', item };
    const product = products[productId];
    if (!product) return { kind: 'gone', item, reason: 'unavailable' };
    const variant = product.variants.find((one) => one.id === item.variant_id);
    return variant ? { kind: 'card', item, product, variant } : { kind: 'gone', item, reason: 'variant' };
  });
}

/*
 * 收藏卡上的颜色 / 尺码下拉框(施工单第 10 步,智鹏 2026-09-14 拍板「收藏页上能选颜色和尺码」)。以下四个纯函数只看数据,
 * choice = 每个选项此刻选的值(按商品选项的顺序),'' = 还没选。
 */

/** 下拉框一开始选什么:商品页收的(pdp)= 收藏的那件全部选好;从商品卡收的(collection)= 只选颜色那一项(卡上只有颜色可选),
 *  其余空着等客人选。只有一个值的项(如只有 F 码)一律算选好 —— 没得选,不许让客人「选」一下才能加购(2026-09-15 独立审查)。
 *  colourName = 颜色那一项的名字(Liquid 给);这款没有这一项 → 除了只有一个值的,一项都不预选(宁可让客人自己选)。 */
export function initialChoice(product, variant, item, colourName) {
  const options = product?.options || [];
  const only = (option) => (option.values.length === 1 ? option.values[0] : '');
  if (!variant || variant.options.length !== options.length) return options.map(only);
  if (item?.added_from !== 'collection') return [...variant.options];
  return options.map((option, i) => (option.name === colourName ? variant.options[i] : only(option)));
}

/** 按此刻已选的那几项(没选的不限),还有没有买得到的变体。没有 = 这张卡怎么选都买不了 →「在庫切れ」,
 *  不是「还没选齐」(卡上收的颜色整色卖光时,原来灰着「カートに追加」、点了跳到一个一项都选不了的尺码框;2026-09-15 独立审查 F1)。 */
export function anyBuyable(product, choice) {
  return product.variants.some((variant) => variant.available && variant.options.every((value, i) => !choice[i] || value === choice[i]));
}

/** 每一项都选了、而且这个组合存在 → 那个变体;否则 null。 */
export function variantFor(product, choice) {
  if (!choice.length || !choice.every(Boolean)) return null;
  return product.variants.find((variant) => variant.options.every((value, i) => value === choice[i])) || null;
}

/** 卡片此刻显示谁的图、价格、链接(加购另按 variantFor):已选那几项对得上的第一个变体;对不上(选的组合不存在)
 *  就少看最后一项再找 —— 颜色选了,至少显示那个颜色的图。一项都没选 = 第一个变体;没有变体 = null。 */
export function previewFor(product, choice) {
  for (let upto = choice.length; upto >= 0; upto -= 1) {
    const found = product.variants.find((variant) =>
      variant.options.every((value, i) => i >= upto || !choice[i] || value === choice[i])
    );
    if (found) return found;
  }
  return null;
}

/** 第 index 项选 value 时有没有买得到的 —— 只看它前面几项此刻的选择(没选的不限):颜色在第一项 = 按「整色有没有货」,
 *  尺码在后 = 按「这个颜色的这个尺码」,同 2点セット。下拉框里的「（在庫切れ）」与禁选都按它。 */
export function valueAvailable(product, choice, index, value) {
  return product.variants.some(
    (variant) =>
      variant.available &&
      variant.options[index] === value &&
      variant.options.every((one, i) => i >= index || !choice[i] || one === choice[i])
  );
}

/** 一次请求,整段(含读回包)10 秒超时;超时 / 网络失败抛错,由调用方当失败处理。 */
async function request(url, init, read) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), FETCH_MS);
  try {
    const res = await fetch(url, { ...init, signal: controller.signal });
    return { res, body: await read(res) };
  } finally {
    clearTimeout(timer);
  }
}

/** 一批取数:网络 / 超时 / 非 2xx / 根标记 / JSON 任何一样不对都回 { ok:false }。 */
async function fetchResults(url) {
  try {
    const { res, body } = await request(
      url,
      { cache: 'no-store', credentials: 'same-origin', headers: { Accept: 'text/html' } },
      (r) => r.text()
    );
    return res.ok ? parseResults(body) : { ok: false, products: null };
  } catch {
    return { ok: false, products: null };
  }
}

function cartRoutes() {
  const routes = window.Theme?.routes || {};
  return { add: routes.cart_add_url || '/cart/add.js', cart: `${routes.cart_url || '/cart'}.js` };
}

/** 读整车,失败重试一次;两次都读不到 → null。 */
async function readCart(url) {
  for (let i = 0; i < 2; i += 1) {
    try {
      const { res, body } = await request(
        url,
        { cache: 'no-store', credentials: 'same-origin', headers: { Accept: 'application/json' } },
        (r) => (r.ok ? r.json() : null)
      );
      if (res.ok && body && Array.isArray(body.items)) return body;
    } catch {
      /* 再试一次 */
    }
  }
  return null;
}

const quantityOf = (lines, variantId) =>
  (lines || []).reduce((sum, line) => (toId(line?.variant_id ?? line?.id) === variantId ? sum + (Number(line.quantity) || 0) : sum), 0);

/** 本页的加购在途(模块级:整页同时只许一个;换成新实例也挡得住,同 2点セット) */
let cartBusy = false;
/** wishlist_view 本次页面加载发过没有 */
let viewTracked = false;

/** 加购一件(这个色码尺码 × 1),返回 true / false。成败以购物车的实际变化为准,见上方死约。 */
async function addOne(variantId) {
  const { add, cart } = cartRoutes();
  const before = await readCart(cart);
  if (!before) return false; // 没有加购前的基线,回包一丢就分不清加没加上 → 不发
  const deferred = CartLinesUpdateEvent.createPromise();
  document.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: variantId, quantity: 1 }],
      promise: deferred.promise,
      detail: { source: CART_SOURCE },
    })
  );
  let posted = false;
  try {
    const { res, body } = await request(
      add,
      {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] }),
      },
      (r) => r.json().catch(() => ({}))
    );
    posted = res.ok && !body?.status;
  } catch {
    posted = false; // 超时 / 网络:看下面的前后差
  }
  const after = await readCart(cart);
  const ok = posted || (after !== null && quantityOf(after.items, variantId) >= quantityOf(before.items, variantId) + 1);
  // 读不到整车:不给 items,页头数字由 cart-icon 自己去重读(不 reject,否则页头不更新)
  let payload = { cart: undefined, detail: { source: CART_SOURCE, sourceId: CART_SOURCE, didError: !ok } };
  if (after) {
    try {
      payload = {
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(after),
        detail: { source: CART_SOURCE, sourceId: CART_SOURCE, items: after.items, didError: !ok },
      };
    } catch {
      // 打包整车出错(整车形状意外):照「读不到整车」那样结算 —— 这个 promise 必须结算,页头 / 订单打标 / 赠品同步都在等它
      // (上线前复审 F5:原来这里一抛,promise 永远不结算、整页加购锁也放不掉)
    }
  }
  deferred.resolve(payload);
  return ok;
}

class AyunelWishlistPage extends HTMLElement {
  // 加购在途时点的卡,按点击先后排队(同一页同一张只排一次),前一件回来后依次加。和 cartBusy 同一层(模块级,
  // 页面实例被主题编辑器重挂也只有一条队);页面卸载时它排的全部作废(补跑框独立审查员:原来只排最新点的一张,中间那张被顶掉 = 少加一件)
  static #queue = [];
  #controller = null;
  #products = Object.create(null); // 商品 id → 商品 | null(取数成功但里面没有它)
  #failed = new Set(); // 这一批取数失败的商品 id(不等于商品不存在)
  #loading = new Set(); // 取数在途的商品 id
  #nodes = new Map(); // variant_id → 卡片 <li>
  #entries = new Map(); // variant_id → 最近一次的出卡结果
  #locked = new Set(); // 正在删除 / 加购(含「カートに追加しました」那 2.4 秒)的 variant_id
  #retried = new Set(); // 取数失败后已经自动重取过一次的商品 id(只自动重取一次,不打成循环)
  #timers = new Set();
  #shown = false; // 网格出过一次了
  #sayTimer = 0;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.addEventListener('click', this.#onClick, { signal });
    this.addEventListener('change', this.#onChange, { signal });
    window.addEventListener('online', () => this.#refetchFailed(), { signal }); // 网络恢复:重取失败过的
    const unsubscribe = store.subscribe(() => {
      this.#trackView();
      this.#sync();
    });
    signal.addEventListener('abort', unsubscribe);
    this.#trackView();
    this.#sync();
  }

  /** wishlist_view 每次页面加载只发一次;账号模式首轮同步还没结果、或这一页已不属于当前登录的人时先不发(此刻的件数不是真的,第 9 步)。 */
  #trackView() {
    if (viewTracked || wishlistPending() || syncStopped() === 'owner') return;
    viewTracked = true;
    track('wishlist_view', { item_count: store.count() });
  }

  disconnectedCallback() {
    this.#controller?.abort();
    AyunelWishlistPage.#queue = AyunelWishlistPage.#queue.filter((one) => {
      if (one.page !== this) return true;
      this.#nodes.get(one.key)?.querySelector('[data-ayn-wl-atc]')?.removeAttribute('aria-busy');
      return false;
    });
    for (const timer of this.#timers) clearTimeout(timer);
    this.#timers.clear();
    clearTimeout(this.#sayTimer);
  }

  /** 页面打开 / 收藏有变化:补取新出现的商品,再按当前清单重画。取数失败过的 id 在这里重取(结果回来前照旧显示占位)。 */
  #sync() {
    const ids = [...new Set(store.list().map((item) => toId(item.product_id)).filter(Boolean))];
    const fresh = ids.filter((id) => !(id in this.#products) && !this.#loading.has(id));
    if (fresh.length) this.#fetch(fresh);
    this.#render();
  }

  async #fetch(ids) {
    for (const id of ids) this.#loading.add(id);
    await Promise.all(
      buildResultUrls(ids, this.dataset.aynSearchUrl).map(async ({ ids: batch, url }) => {
        const result = await fetchResults(url);
        for (const id of batch) {
          this.#loading.delete(id);
          if (result.ok) {
            this.#products[id] = result.products[id] || null;
            this.#failed.delete(id);
          } else {
            this.#failed.add(id);
          }
        }
      })
    );
    this.#render();
    // 取数失败的:过 3 秒自动重取一次(上线前复审 F2:原来只有收藏本身变了才重取,客人看到一排「取不到」、旁边就是「削除」,
    // 很可能把还在卖的商品自己删掉)。每个 id 本页只自动重取一次;之后网络恢复(online)再取。「不自动删收藏」不变。
    const again = ids.filter((id) => this.#failed.has(id) && !this.#retried.has(id));
    if (!again.length || this.#controller?.signal.aborted) return; // 页面已卸载:不再排自动重取
    for (const id of again) this.#retried.add(id);
    const timer = setTimeout(() => {
      this.#timers.delete(timer);
      this.#refetchFailed(again);
    }, REFETCH_MS);
    this.#timers.add(timer);
  }

  /** 唯一的重画:件数行、空态、网格(按 variant_id 复用卡片,顺序不对才移动)。
   *  账号模式首轮同步还没结果、本机也没有这位客人的缓存(wishlistPending)→ 「空」不等于「没有收藏」:件数行与空态都不出、
   *  宿主挂 aria-busy,等同步有结果(store 会通知一次)再画(第 9 步)。 */
  #render() {
    const items = store.list();
    const pending = wishlistPending();
    // 这一页已不属于当前登录的人(换人 / 登出后的旧页):存储层已把收藏收起,「空」不是「没有收藏」——
    // 不出件数行与空态,改出 owner 那句(请刷新);原来那句写进了刚被删掉的卡里,眼睛只看到「还没有收藏」(实现复审 Codex #6)
    const stale = syncStopped() === 'owner';
    const staleLine = this.querySelector('[data-ayn-wl-stale]');
    if (staleLine) {
      const text = stale ? failNotice('owner') : '';
      if (staleLine.textContent !== text) staleLine.textContent = text;
      staleLine.hidden = text === '';
    }
    const count = this.querySelector('[data-ayn-wl-count]');
    if (count) {
      count.textContent = (this.dataset.aynMsgCount || '').replace('{count}', String(items.length));
      count.hidden = pending || stale || items.length === 0;
    }
    const empty = this.querySelector('[data-ayn-wl-empty]');
    if (empty) empty.hidden = pending || stale || items.length !== 0;
    if (pending) {
      this.setAttribute('aria-busy', 'true');
      return;
    }
    const grid = this.querySelector('[data-ayn-wl-grid]');
    if (!grid) return;

    const entries = resolveCards(items, this.#products, this.#failed);
    if (!this.#shown && entries.some((entry) => entry.kind === 'pending')) {
      grid.hidden = true;
      this.setAttribute('aria-busy', 'true'); // 第一轮取数在途:网格还不出,读屏知道在加载
      return;
    }
    this.removeAttribute('aria-busy');
    const wanted = new Map();
    for (const entry of entries) if (entry.kind !== 'pending') wanted.set(entry.item.variant_id, entry);
    for (const [key, node] of this.#nodes) {
      if (wanted.has(key)) continue;
      node.remove();
      this.#nodes.delete(key);
      this.#entries.delete(key);
    }
    let cursor = grid.firstElementChild;
    for (const [key, entry] of wanted) {
      let node = this.#nodes.get(key);
      if (node && node.dataset.aynWlKind !== entry.kind) {
        if (node === cursor) cursor = node.nextElementSibling;
        node.remove();
        node = null;
      }
      if (!node) {
        node = this.#make(entry.kind, key);
        if (!node) continue;
        this.#nodes.set(key, node);
      }
      this.#entries.set(key, entry);
      this.#fill(node, entry);
      if (node === cursor) cursor = node.nextElementSibling;
      else grid.insertBefore(node, cursor);
    }
    grid.hidden = this.#nodes.size === 0;
    if (this.#nodes.size) this.#shown = true;
  }

  /** 前一件加购回来后,按先后执行下一张排着的(页面已卸载 / 卡已不在 / 变成不能加的,就跳过、收起转圈,接着看下一张)。 */
  static #runNext() {
    while (AyunelWishlistPage.#queue.length) {
      const next = AyunelWishlistPage.#queue.shift();
      if (next.page.#controller?.signal.aborted) continue;
      const node = next.page.#nodes.get(next.key);
      const button = node?.querySelector('[data-ayn-wl-atc]');
      if (!node || !button) continue;
      button.removeAttribute('aria-busy');
      next.page.#addToCart(next.key, node, button);
      if (cartBusy) return; // 这张开始加了:它回来后会再叫下一张
    }
  }

  /** 重取取数失败过的商品(还在收藏里、没在途的);ids 不给 = 全部失败过的。 */
  #refetchFailed(ids = [...this.#failed]) {
    const live = new Set(store.list().map((item) => toId(item.product_id)));
    const todo = ids.filter((id) => this.#failed.has(id) && live.has(id) && !this.#loading.has(id));
    if (todo.length) this.#fetch(todo);
  }

  /** 从 Liquid 的模板克隆一张卡(kind:'card' 商品卡 / 'gone' 占位卡)。 */
  #make(kind, key) {
    const template = this.querySelector(`template[data-ayn-wl-tpl="${kind}"]`);
    const node = template?.content?.firstElementChild?.cloneNode(true);
    if (!node) return null;
    node.dataset.aynWlKind = kind;
    node.dataset.aynWlVariant = key;
    return node;
  }

  /** 填一张卡:只改属性、文字与 hidden,不增删节点。 */
  #fill(node, entry) {
    const key = entry.item.variant_id;
    const remove = node.querySelector('[data-ayn-wl-remove]');
    if (entry.kind === 'gone') {
      for (const text of node.querySelectorAll('[data-ayn-wl-gone]')) {
        const on = text.dataset.aynWlGone === entry.reason;
        text.hidden = !on;
        text.id = `AynWlGone-${text.dataset.aynWlGone}-${key}`;
        if (on) remove?.setAttribute('aria-describedby', text.id);
      }
      return;
    }
    const { product } = entry;
    const { choice, variant } = this.#choose(node, entry);
    // 卡片显示的那个变体:选齐了 = 选中的;没选齐 = 已选那几项对得上的第一个(颜色选了就是那个颜色的图)
    const shown = variant || previewFor(product, choice) || entry.variant;
    for (const link of node.querySelectorAll('[data-ayn-wl-link]')) {
      if (shown.url) link.setAttribute('href', shown.url);
      else link.removeAttribute('href');
    }
    const image = node.querySelector('[data-ayn-wl-img]');
    const src = shown.img?.src || '';
    if (image && image.getAttribute('src') !== src) {
      if (src) {
        image.setAttribute('srcset', shown.img.srcset);
        image.setAttribute('src', src);
      } else {
        image.removeAttribute('srcset');
        image.removeAttribute('src');
      }
    }
    const title = node.querySelector('[data-ayn-wl-title]');
    if (title) {
      title.textContent = product.title;
      title.id = `AynWlTitle-${key}`;
      remove?.setAttribute('aria-describedby', title.id);
    }
    const price = node.querySelector('[data-ayn-wl-price]');
    if (price) price.textContent = shown.price;
    this.#paintSelects(node, product, choice);
    this.#lockSelects(node, this.#locked.has(key));
    if (this.#locked.has(key)) return;
    let state = 'choose';
    if (variant) state = variant.available ? 'buy' : 'soldout';
    else if (choice.every(Boolean) || !anyBuyable(product, choice)) state = 'soldout';
    if (state !== 'buy') this.#cancelQueued(key, node);
    this.#paintCart(node, state);
  }

  /** 加购在途 + 成功句那 2.4 秒:这张卡的下拉框锁住 —— 不锁的话客人途中把 S 改成 M,加进车的是 S,
   *  「カートに追加しました」却挂在写着 M 的卡下面(2026-09-15 独立审查 F5)。放锁走 #fill(失败分支 / 2.4 秒后的重画)。 */
  #lockSelects(node, locked) {
    for (const select of node.querySelectorAll('select[data-ayn-wl-opt]')) select.disabled = locked;
  }

  /** 排着队的这张卡变成加不了了(客人改成没货 / 没选齐):这一下作废、收起转圈 —— 轮到它时本来也不会加,
   *  不收的话客人会看着一个灰键上的白圈转到前一件回来(补跑写守卫那路实测)。 */
  #cancelQueued(key, node) {
    const queue = AyunelWishlistPage.#queue;
    AyunelWishlistPage.#queue = queue.filter((one) => one.page !== this || one.key !== key);
    if (AyunelWishlistPage.#queue.length !== queue.length) node.querySelector('[data-ayn-wl-atc]')?.removeAttribute('aria-busy');
  }

  /** 这张卡此刻选的是什么 → { choice, variant }(variant = 加购要加的那个,没选齐 / 没这个组合 = null)。
   *  下拉框第一次出卡时按 initialChoice 摆好,之后只读客人此刻的选择。只有一个值的项不出下拉框、直接算选好;
   *  没有选项的商品(只有默认变体)没有下拉框,加的就是收藏的那一件。 */
  #choose(node, entry) {
    const { product } = entry;
    if (!product.options.length) return { choice: [], variant: entry.variant };
    const box = node.querySelector('[data-ayn-wl-selects]');
    if (box && !box.dataset.aynWlBuilt) this.#buildSelects(box, entry);
    const choice = product.options.map((option) => (option.values.length === 1 ? option.values[0] : ''));
    for (const select of node.querySelectorAll('select[data-ayn-wl-opt]')) {
      const index = Number(select.dataset.aynWlOpt);
      if (index >= 0 && index < choice.length) choice[index] = select.value;
    }
    return { choice, variant: variantFor(product, choice) };
  }

  /** 从 select / option 两个模板克隆出这款的下拉框(每个不止一个值的商品选项一个,前面一项「…を選択」),按 initialChoice 选好。
   *  只有一个值的项不出下拉框(同官方商品页选择器:只有一个值就写成字、不给选;这里连字也不写,图与商品名已说明)。只做一次。 */
  #buildSelects(box, entry) {
    const { product } = entry;
    const selectTpl = this.querySelector('template[data-ayn-wl-tpl="select"]')?.content?.firstElementChild;
    const optionTpl = this.querySelector('template[data-ayn-wl-tpl="option"]')?.content?.firstElementChild;
    if (!selectTpl || !optionTpl) return;
    box.dataset.aynWlBuilt = '1';
    const initial = initialChoice(product, entry.variant, entry.item, this.dataset.aynColorOption);
    product.options.forEach((option, index) => {
      if (option.values.length === 1) return;
      const wrap = selectTpl.cloneNode(true);
      const select = wrap.querySelector('select');
      if (!select) return;
      select.dataset.aynWlOpt = String(index);
      select.setAttribute('aria-label', `${product.title} - ${option.name}`);
      const placeholder = optionTpl.cloneNode(true);
      placeholder.value = '';
      placeholder.disabled = true;
      placeholder.textContent = (this.dataset.aynMsgChoose || '').replace('{option}', option.name);
      select.append(placeholder);
      for (const value of option.values) {
        const one = optionTpl.cloneNode(true);
        one.value = value;
        one.dataset.aynWlBase = value;
        one.textContent = value;
        select.append(one);
      }
      select.value = initial[index];
      if (initial[index] === '' || select.value !== initial[index]) placeholder.selected = true; // 没预选 / 收藏的值不在选项里:当没选
      box.append(wrap);
    });
    box.hidden = !box.querySelector('select[data-ayn-wl-opt]'); // 每一项都只有一个值:一个下拉框都没有,容器照旧收起
  }

  /** 下拉框里每个值标不标「（在庫切れ）」、禁不禁选(第一项只标不禁,后面几项买不到的禁选 —— 同 2点セット);
   *  选中的值买不到时那个框挂 data-ayn-soldout(变淡 + 删除线)。有没有货只看它前面几项的选择(valueAvailable)。 */
  #paintSelects(node, product, choice) {
    const suffix = this.dataset.aynMsgSoldSuffix || '';
    for (const select of node.querySelectorAll('select[data-ayn-wl-opt]')) {
      const index = Number(select.dataset.aynWlOpt);
      for (const option of select.options) {
        const base = option.dataset.aynWlBase;
        if (base === undefined) continue; // 「…を選択」那一项
        const ok = valueAvailable(product, choice, index, base);
        const text = ok ? base : `${base}${suffix}`;
        if (option.textContent !== text) option.textContent = text;
        option.disabled = index > 0 && !ok && base !== choice[index]; // 选中的那个不禁(客人一眼看得到它没货)
      }
      select.toggleAttribute('data-ayn-soldout', choice[index] !== '' && !valueAvailable(product, choice, index, choice[index]));
    }
  }

  /** 加购钮平常的样子(成功句那 2.4 秒结束也走这里复原,摘掉 .is-added):
   *  'buy' 买得到 =「カートに追加」;'soldout' 选齐了买不到 / 没这个组合 =「在庫切れ」+ disabled 与 aria-disabled 双写(手册 §C1.4);
   *  'choose' 还没选齐 =「カートに追加」、灰着(aria-disabled)但**不写 disabled** —— disabled 的键收不到点击,
   *  客人点了没反应;不写它,点一下就把焦点带到空着的下拉框(#pointToChoice,brand-crm 2026-09-14 INFO)。 */
  #paintCart(node, state) {
    const button = node.querySelector('[data-ayn-wl-atc]');
    const label = node.querySelector('[data-ayn-wl-atc-label]');
    if (!button || !label) return;
    button.classList.toggle('is-added', false);
    button.removeAttribute('aria-label');
    button.disabled = state === 'soldout';
    if (state === 'buy') button.removeAttribute('aria-disabled');
    else button.setAttribute('aria-disabled', 'true');
    const text = (state === 'soldout' ? this.dataset.aynMsgSoldOut : this.dataset.aynMsgAtc) || '';
    if (label.textContent !== text) label.textContent = text;
  }

  /** 还没选齐时点了灰着的加购键:焦点带到第一个空着的下拉框,浏览器支持就直接打开选单。返回 true = 这一下拿去指路了、不加购。 */
  #pointToChoice(node) {
    const empty = [...node.querySelectorAll('select[data-ayn-wl-opt]')].find((select) => select.value === '');
    if (!empty) return false;
    empty.focus();
    try {
      empty.showPicker?.(); // 不支持 / 浏览器拒绝:焦点已经带到那里(框变墨色边)
    } catch {
      /* 同上 */
    }
    return true;
  }

  /** 客人改了颜色 / 尺码:收起这张卡的错误行,按新的选择重填(图、价格、链接、加购钮)。 */
  #onChange = (event) => {
    const select = event.target instanceof Element ? event.target.closest('select[data-ayn-wl-opt]') : null;
    if (!select || !this.contains(select)) return;
    const node = select.closest('[data-ayn-wl-variant]');
    const entry = node ? this.#entries.get(node.dataset.aynWlVariant) : null;
    if (entry?.kind !== 'card') return;
    this.#showError(node, '');
    this.#fill(node, entry);
  };

  #onClick = (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-ayn-wl-remove], [data-ayn-wl-atc]') : null;
    if (!button || !this.contains(button)) return;
    const node = button.closest('[data-ayn-wl-variant]');
    const key = node?.dataset.aynWlVariant;
    if (!key) return;
    if (button.hasAttribute('data-ayn-wl-remove')) {
      this.#remove(key, node);
      return;
    }
    if (button.disabled) return; // 在庫切れ:真浏览器里 disabled 的键本来就收不到点击,这里也不指路、不加购
    if (!this.#pointToChoice(node)) this.#addToCart(key, node, button);
  };

  async #remove(key, node) {
    if (this.#locked.has(key)) return;
    this.#locked.add(key);
    this.#showError(node, '');
    const item = store.list().find((one) => one.variant_id === key);
    const grid = this.querySelector('[data-ayn-wl-grid]');
    const index = grid ? [...grid.children].indexOf(node) : -1;
    const result = await store.remove(key);
    this.#locked.delete(key);
    if (!result.ok) {
      if (result.reason === 'readonly') return; // 主题编辑器里:不写、不提示
      // 账号模式停发(开关关着 / 结果不明)/ 这一页已不属于当前登录的人:换成 failNotice 那两句(模板里那一行本身不带字,第 9 步)
      const notice = failNotice(result.reason);
      const line = notice ? node.querySelector('[data-ayn-wl-error="sync"]') : null;
      if (line) line.textContent = notice;
      const which = line ? 'sync' : 'remove';
      this.#showError(node, which);
      this.#say(this.#errorText(node, which));
      return;
    }
    this.#render(); // 通常 subscribe 已经重画过;再画一次保证「删成功 = 卡没了」不靠通知先后
    this.#say(this.dataset.aynMsgRemoved);
    this.#refocus(index);
    if (result.reason !== '' || !item) return; // 'unchanged' = 别处已经删了,不计数
    track('wishlist_remove', {
      product_id: item.product_id,
      product_handle: item.product_handle,
      variant_id: item.variant_id,
      item_count: store.count(),
      source: 'wishlist_page',
    });
  }

  async #addToCart(key, node, button) {
    const entry = this.#entries.get(key);
    // 加的是下拉框此刻选中的那个变体(排队的那一下也在轮到时现读);没选齐 / 买不到就不加
    const variant = entry?.kind === 'card' ? this.#choose(node, entry).variant : null;
    if (this.#locked.has(key) || button.disabled || !variant?.available) return;
    if (cartBusy) {
      // 别的卡正在加购:整页同一时刻只许一个加购在途 —— 这一下不丢,按点击先后排队(先转圈),前一件回来后依次加
      // (上线前复审 F5:原来直接 return,不转圈、不提示、也没加进车,和「点心没反应」同一类)
      if (!AyunelWishlistPage.#queue.some((one) => one.page === this && one.key === key)) AyunelWishlistPage.#queue.push({ page: this, key });
      button.setAttribute('aria-busy', 'true');
      return;
    }
    cartBusy = true;
    this.#locked.add(key);
    this.#lockSelects(node, true);
    this.#showError(node, '');
    button.setAttribute('aria-busy', 'true');
    let ok = false;
    try {
      ok = await addOne(variant.id);
    } catch {
      ok = false; // 意外出错:当失败处理(下面放锁、出错误句),不许把整页加购键锁死到刷新
    } finally {
      cartBusy = false;
      button.removeAttribute('aria-busy');
    }
    AyunelWishlistPage.#runNext();
    if (!ok) {
      this.#locked.delete(key);
      this.#lockSelects(node, false);
      this.#showError(node, 'atc');
      this.#say(this.#errorText(node, 'atc'));
      return;
    }
    // 成功句写在模板里(与平常的字叠在同一格,CSS 按 .is-added 切换,按钮不长高);读屏播报与按钮名称取同一段字 —— 成功句只有这一处。
    // 按钮名称另挂 aria-label:两段 inline-block 算名称时 Chrome 会在中间插一个空格(「カートに追加 しました」,DEV 实测)
    const added = node.querySelector('[data-ayn-wl-atc-done]')?.textContent || '';
    button.classList.toggle('is-added', true);
    if (added) button.setAttribute('aria-label', added);
    this.#say(added);
    track('wishlist_purchase_intent', {
      product_id: entry.product.id,
      product_handle: entry.product.handle,
      variant_id: variant.id,
    });
    const timer = setTimeout(() => {
      this.#timers.delete(timer);
      this.#locked.delete(key);
      this.#render();
    }, ADDED_MS);
    this.#timers.add(timer);
  }

  /** 卡下错误行:which = 'atc' / 'remove' 显示那一行,'' 全部收起(下次在这张卡上点任何钮时收起)。 */
  #showError(node, which) {
    for (const line of node.querySelectorAll('[data-ayn-wl-error]')) line.hidden = line.dataset.aynWlError !== which;
  }

  #errorText(node, which) {
    return node.querySelector(`[data-ayn-wl-error="${which}"]`)?.textContent || '';
  }

  /** 焦点随删掉的卡一起没了(落到 body)时:落到同一位置那张卡的删除钮;删空了落到页标题。焦点还在别处就不动。 */
  #refocus(index) {
    const active = document.activeElement;
    if (active && active !== document.body) return;
    const grid = this.querySelector('[data-ayn-wl-grid]');
    const cards = grid && !grid.hidden ? [...grid.children] : [];
    const next = cards[Math.min(Math.max(index, 0), cards.length - 1)];
    (next?.querySelector('[data-ayn-wl-remove]') || this.querySelector('.ayunel-wl-page__title'))?.focus();
  }

  /** 读屏播报:先清空、隔 SAY_GAP_MS 再写(同一句连说两次也会再念,同商品页的心)。 */
  #say(text) {
    const region = this.querySelector('[data-ayn-wl-status]');
    if (!region) return;
    clearTimeout(this.#sayTimer);
    region.textContent = '';
    if (text) {
      this.#sayTimer = setTimeout(() => {
        region.textContent = text;
      }, SAY_GAP_MS);
    }
  }
}

if (!customElements.get(PAGE_TAG)) customElements.define(PAGE_TAG, AyunelWishlistPage);

/* ---------------- <ayunel-wishlist-count>:header 右上角的心与数字 ---------------- */

/*
 * 结构与样式全在 snippets/ayunel-header-wishlist.liquid(官方 header-actions 里 render 一次;宿主双挂 morph skip)。
 * 这里只做一件事:订阅 store.count(),写气泡里的数字、切 hidden、换链接的读屏名称 —— 一个节点都不造、不删;
 * 不用 ref / on:(header-actions 是 Component,会收走后代身上的 ref)。
 * 死约(施工单第 4 步 · 方案 §2.3 Header 行):
 * - 数字 = store.count(),与收藏页件数、商品页的心同一个来源;本页写的、别的标签页改的、返回键恢复的都经 subscribe 进来,
 *   这里不自己听 storage / pageshow。
 * - 0 件 → 气泡清空并 hidden(不出「0」);1–99 → 原数;≥100 →「99+」(存储上限 100 件)。
 * - 读屏名称带真实件数(气泡 aria-hidden):0 件 = data-label;有收藏 = data-label-count 里的 {count} 换成真实件数
 *   (气泡显示「99+」时照样读 100)。句子全在 Liquid,这里不写一个日语字。
 * - 主题编辑器里整段 header 被换掉时,新节点的 connectedCallback 重新订阅、重新填;卸载时退订。
 */
const COUNT_TAG = 'ayunel-wishlist-count';
const BUBBLE_MAX = 99; // 超过就显示「99+」(同官方购物车气泡 limit 100 的上限)

class AyunelWishlistCount extends HTMLElement {
  #unsubscribe = null;

  connectedCallback() {
    this.#unsubscribe?.();
    this.#unsubscribe = store.subscribe(() => this.#paint());
    this.#paint();
  }

  disconnectedCallback() {
    this.#unsubscribe?.();
    this.#unsubscribe = null;
  }

  /** 唯一的上色函数:只改气泡文字、hidden 与链接的 aria-label。 */
  #paint() {
    const count = store.count();
    const bubble = this.querySelector('.ayunel-hwl__bubble');
    if (bubble) {
      const text = count === 0 ? '' : count > BUBBLE_MAX ? `${BUBBLE_MAX}+` : String(count);
      if (bubble.textContent !== text) bubble.textContent = text;
      bubble.hidden = count === 0;
    }
    const link = this.querySelector('.ayunel-hwl__link');
    const label = count === 0 ? this.dataset.label : (this.dataset.labelCount || '').replace('{count}', String(count));
    if (link && label) link.setAttribute('aria-label', label);
  }
}

if (!customElements.get(COUNT_TAG)) customElements.define(COUNT_TAG, AyunelWishlistCount);

/* ---------------- 订单打标:购物车属性 _ayn_wl ---------------- */

/*
 * 施工单第 7 步 · 方案 §2.3「订单打标」行;口径与局限写在 08-themes/wishlist-src/README.md(拿订单数据下结论前先看)。
 * 值 = `收藏件数|车里命中的收藏 variant_id`(命中的按收藏清单顺序 = 最新收藏的在前,逗号分隔),例 `3|46012345678901`;
 *      一件都没命中 = `3|`;收藏清空 = ''(Shopify 用空串删掉这个属性)。只存件数 + 命中摘要,不塞整份清单。
 * 死约:
 * - 只经 window.aynMarkCart 写(全站唯一写购物车属性的出口 assets/ayunel-track.js:节流 700ms、没车不抢建、失败重试、
 *   同一辆车同值不重发都在那边);这里绝不自己请求 /cart/update.js(守卫 tests/test_theme_wishlist_store.py 文本级)。
 * - 两个触发点;页面打开时不打(换车一定会经过一次加购,track 的发成功记录又绑了购物车 token,第一次加购就补上):
 *   (a) 购物车改了 —— 标准事件 shopify:cart:lines-update(商品页加购 / 购物车改数量·删除 / 2点セット / 收藏页加购):
 *       等 promise 结算;rejected 或 didError → 不动(结果不明,不替客人写);收藏是空的 → 不动。车里商品取结算结果的 detail.items,
 *       拿不到才读一次 /cart.js。赠品系统(assets/ayunel-gift-sync-v2.js,source 'ayunel-gift-sync')自己改车发的事件不理:
 *       它只加减赠品行(赠品带 cart-hidden、不出心,不会在收藏里),命中不变;理了反而会在它改车途中插进一次 /cart/update.js。
 *   (b) 收藏变了 —— store.subscribe(本页 / 返回键恢复;别的标签页改的不写,理由见文件末尾;账号模式服务器来的整单替换
 *       source 'sync' 也不写 —— 页面打开时不改车,第 9 步):读一次 /cart.js 重算;变空了直接发 ''(不用读车)。
 * - 先后按「车数据是什么时候读到的」排,不按触发先后、也不按到达先后:(a) 记在结算那一刻,(b) 记在开始读车那一刻;
 *   手上已经写过更晚读到的车,这份就作废。只按到达先后 →「点心那次读车很慢、期间加购结算了」,慢的那份旧车后到、把命中盖掉;
 *   只按触发先后(代号)→「加购在途时点心」,点心那次先写,加购结算那份更完整的反被丢掉。两种改坏法守卫里都有。
 *   收藏清单一律在写的那一刻现取 store.list()。
 * - 车读不到(两次都失败)→ 这次不动,不拿半截数据去盖。
 * - 主题编辑器里(Shopify.designMode)不打标:编辑器预览与店面同源,写的是真实购物车。
 */
const TAG_KEY = '_ayn_wl';
const GIFT_SOURCE = 'ayunel-gift-sync'; // assets/ayunel-gift-sync-v2.js 的 SOURCE
let tagClock = 0; // 逻辑时钟:车数据每「读到 / 开始读」一次 +1
let tagAt = 0; // 已经写出去的那份车数据的时刻

/** 唯一调 aynMarkCart 的地方。at:这份车数据的时刻;lines:车里的行(/cart.js 的 items),不是数组 = 车没读到 → 不动。 */
function tagCart(at, lines) {
  if (at <= tagAt || !Array.isArray(lines)) return;
  tagAt = at;
  const items = store.list();
  let value = '';
  if (items.length) {
    const inCart = new Set(lines.map((line) => toId(line?.variant_id ?? line?.id)).filter(Boolean));
    const hits = items.filter((item) => inCart.has(item.variant_id)).map((item) => item.variant_id);
    value = `${items.length}|${hits.join(',')}`;
  }
  try {
    window.aynMarkCart?.(TAG_KEY, value);
  } catch {
    /* 打标坏了不影响收藏与购物车 */
  }
}

/** 两个触发点的共同入口:lines 给了就用(加购结算结果),没给就读一次 /cart.js。 */
function retag(lines) {
  if (window.Shopify?.designMode) return;
  const at = ++tagClock;
  if (Array.isArray(lines)) {
    tagCart(at, lines);
    return;
  }
  readCart(cartRoutes().cart).then((cart) => tagCart(at, cart?.items));
}

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  if (event?.detail?.source === GIFT_SOURCE) return; // 赠品系统构造事件时就带 source(它自己也靠这个短路)
  const promise = event?.promise;
  if (!promise || typeof promise.then !== 'function') return;
  promise.then(
    (result) => {
      const detail = result?.detail;
      if (detail?.didError || detail?.source === GIFT_SOURCE) return;
      // 收藏是空的 → 没有要写的(从有到空的删除归 (b))。不写这一句,绝大多数没收藏的客人每辆车第一次加购都会
      // 多一次 /cart/update.js(DEV 实测 2026-09-14),购买主路径上白多一个改车请求
      if (store.count() === 0) return;
      retag(detail?.items);
    },
    () => {} // rejected:这次改车结果不明,不动
  );
});

// 别的标签页改的收藏(source 'tab')这里不写:改的那一页自己会按同一份收藏写同一辆车;这一页再写一遍是多余的,而且这一页
// 看不见那一页正在进行的加购 —— 那一页「点心后马上加购」时,这一页的写入会和它的加购同时在途(补跑框独立审查员指出;
// 同一页里这样同时在途 DEV 实测 40 次丢件 2 次,见 assets/ayunel-track.js「改车请求在途时不写」)。
// 代价:只有「旧主题的标签页改了收藏」(cutover 那几天才可能)时,要等这一页下一次改车才补上。
// 账号模式服务器来的整单替换(source 'sync':页面加载的 /list·/merge、返回键恢复、停发改快照)同样不写(2026-09-15 第 9 步
// 双脑复审定:页面打开时不改车 —— 别的设备刚收的心,要等这台下一次改车才进打标;换来购买主路径上不多一个改车请求)。
store.subscribe(({ source } = {}) => {
  if (source === 'tab' || source === 'sync') return; // sync = 账号模式服务器来的整单替换:页面打开时不改车,等下一次改车再补
  retag(store.count() === 0 ? [] : undefined);
});
